import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PopupButton } from 'react-calendly';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { SYLLABUS } from '../constants/syllabus';
import { TOPICS } from '../constants/topics';
import { TOPIC_SYLLABUS_MAP, SYLLABUS_SECTIONS } from '../constants/syllabus';
import { VerbQuiz } from './VerbQuiz';

const MAESTRA_EMAIL = 'sayciao.school@outlook.com';
const CALENDLY_URL  = 'https://calendly.com/sayciao-school/lezione';
const QUIZ_TOTAL    = 10;
const VOCAB_CORRECT_MESSAGES = ['Ottimo! ✨', 'Perfetto! 🎉', 'Wow! 👏', 'Esatto! ⭐', 'Eccellente! 🌟', 'Fantastico! 💫'];
const VOCAB_STREAK_MILESTONES = {
  3:  '🔥🔥 3 in a row — you\'re on fire!',
  5: '🔥🔥🔥 5 in a row — incredible!',
  8: '🔥🔥🔥🔥 8 in a row — unstoppable!',
};


const customCalendlyStyles = {
  backgroundColor: '#BC6C25', color: '#fff', border: 'none',
  padding: '16px 32px', borderRadius: '8px', fontSize: '0.75rem',
  fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
  boxShadow: '0 4px 15px rgba(188, 108, 37, 0.2)',
};

// ─── Flashcard ────────────────────────────────────────────────────────────────
const Flashcard = ({ word }) => {
  const [flipped, setFlipped] = useState(false);
  // Support both DB words (word_original) and hardcoded topic words (italian)
  const italian = word.word_original || word.italian || '';
  const english = word.word_translation || word.english || '';

  const speakItalian = (e) => {
    e.stopPropagation();
    const utterance = new SpeechSynthesisUtterance(italian);
    utterance.lang  = 'it-IT';
    utterance.rate  = 0.85;
    const voices    = window.speechSynthesis.getVoices();
    const italianVoice =
      voices.find(v => v.name === 'Alice'    && v.lang === 'it-IT') ||
      voices.find(v => v.name === 'Federica' && v.lang === 'it-IT') ||
      voices.find(v => v.name === 'Luca'     && v.lang === 'it-IT') ||
      voices.find(v => v.name.includes('Google') && v.lang === 'it-IT') ||
      voices.find(v => v.name.includes('Italian') && v.lang === 'it-IT') ||
      voices.find(v => v.lang === 'it-IT') ||
      voices.find(v => v.lang.startsWith('it'));
    if (italianVoice) utterance.voice = italianVoice;
    window.speechSynthesis.speak(utterance);
  };
  return (
    <div style={flashcardContainer} onClick={() => setFlipped(f => !f)} role="button" tabIndex={0} aria-label={`Flashcard: ${italian}. Clicca per girare.`} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFlipped(f => !f); }}>
      <div style={{ ...flashcardInner, ...(flipped ? flashcardFlipped : {}) }}>
      <div style={{ ...flashcardFace, ...flashcardFront }}>
          <button onClick={speakItalian} aria-label={`Pronuncia: ${italian}`} style={audioButtonStyle}>🔊</button>
          {word.emoji && <span style={{ fontSize: '2rem', marginBottom: '6px' }}>{word.emoji}</span>}
          <span style={vocabLabel}>Italiano</span>
          <span style={itWord}>{italian}</span>
        </div>
        <div style={{ ...flashcardFace, ...flashcardBack }}>
          {word.emoji && <span style={{ fontSize: '2rem', marginBottom: '6px' }}>{word.emoji}</span>}
          {!/^\d[\d,.]*$/.test(english) && (
  <span style={vocabLabel}>English</span>
)}
          <span style={enWord}>{english}</span>
          {word.example_sentence && (
            <div style={exampleWrapper}>
              <p style={exampleText}>"{word.example_sentence}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Personal Vocab Quiz ──────────────────────────────────────────────────────
const VocabQuiz = ({ allWords, firstName, topicOverride, onBackToFlashcards }) => {
  // topicOverride: { name, words: [{italian, english}] } — set when quizzing a topic
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentWords = allWords.filter(w => new Date(w.lessonDate) >= thirtyDaysAgo);

  const quizRef = useRef(null);

  useEffect(() => {
    if (quizRef.current) {
      quizRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);
  
  const [quizPool] = useState('recent'); // 'recent' | 'all'  
  const [quizActive,   setQuizActive]   = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [options,      setOptions]      = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [feedback,     setFeedback]     = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [isFinished,   setIsFinished]   = useState(false);
  const [quizWords,    setQuizWords]    = useState([]);
  const [cardKey,           setCardKey]           = useState(0);
  const [quizResolvedPool,  setQuizResolvedPool]  = useState([]);
  const [streak,         setStreak]         = useState(0);
  const [bestStreak,     setBestStreak]     = useState(0);
  const [missedItems,    setMissedItems]    = useState([]);
  const [correctMessage, setCorrectMessage] = useState(VOCAB_CORRECT_MESSAGES[0]);
  const [milestone,      setMilestone]      = useState(null);
  const [answerHistory,  setAnswerHistory]  = useState([]);
  const [quizMode,       setQuizMode]       = useState(null); // 'choice' | 'typing'
  const [typedInput,     setTypedInput]     = useState('');
  const [showHint,       setShowHint]       = useState(false);

  const activePool = quizPool === 'recent' ? recentWords : allWords;

const getTranslation = (w) => w.word_translation || w.english || '';

  // Splits "il/la nipote" → ["il nipote", "la nipote"]
  // and "il cugino / la cugina" or "una macchina / l'automobile / l'auto" → each full phrase
  const splitVariants = (raw) => {
    if (!raw) return [];
    const prefixMatch = raw.match(/^([\p{L}']+)\/([\p{L}']+)\s+(.+)$/u);
    if (prefixMatch) {
      const [, a, b, rest] = prefixMatch;
      return [`${a} ${rest}`, `${b} ${rest}`];
    }
    if (raw.includes(' / ')) {
      return raw.split(' / ').map(s => s.trim()).filter(Boolean);
    }
    return [raw];
  };

  const getOriginal = (w) => {
    const val = w.word_original || w.italian || '';
    const primary = Array.isArray(val) ? val[0] : val;
    return splitVariants(primary)[0] || primary;
  };

  const getAcceptedAnswers = (w) => {
    const val = w.word_original || w.italian || '';
    const list = Array.isArray(val) ? val.flatMap(splitVariants) : splitVariants(val);
    const extras = Array.isArray(w.alt_answers) ? w.alt_answers : [];
    return [...list, ...extras].filter(Boolean);
  };
  
const buildOptions = (word, pool) => {
    const correct = getTranslation(word);
    if (!correct) return;
    // Use manually defined options if provided
    if (word.options && word.options.length === 3) {
      setOptions([...word.options, correct].sort(() => Math.random() - 0.5));
      return;
    }
const wrongs = pool
      .filter(w => {
        const t = getTranslation(w);
        return t && t.toLowerCase() !== correct.toLowerCase();
      })
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map(w => getTranslation(w))
      .filter(Boolean);
    if (wrongs.length < 3) return;
    setOptions([...wrongs, correct].sort(() => Math.random() - 0.5));
  };

  const buildQuiz = (pool = activePool) => {
    const resolvedPool = topicOverride ? topicOverride.words : pool;
    if (resolvedPool.length < 4) return;
    const shuffled = [...resolvedPool].sort(() => Math.random() - 0.5);
    const picked   = shuffled.slice(0, Math.min(QUIZ_TOTAL, shuffled.length));
    setQuizWords(picked);
    setQuizResolvedPool(resolvedPool); // ← store so handleSelect can use it
    setCurrentIndex(0);
    setCorrectCount(0);
    setFeedback(null);
    setSelected(null);
    setIsFinished(false);
    setCardKey(k => k + 1);
     setStreak(0);
    setMissedItems([]);
    setAnswerHistory([]);
    setMilestone(null);
    setTypedInput('');
    setShowHint(false);
    setQuizActive(true);
    if (quizMode === 'choice') buildOptions(picked[0], resolvedPool);
  };

const moveToNext = () => {
    const next = currentIndex + 1;
    if (next >= quizWords.length) {
      setIsFinished(true);
    } else {
      setCurrentIndex(next);
      setFeedback(null);
      setSelected(null);
 setTypedInput('');
      setShowHint(false);
      setCardKey(k => k + 1);
      if (quizMode === 'choice') buildOptions(quizWords[next], quizResolvedPool);
    }
  };

  const handleSelect = (opt) => {
    if (feedback) return;
    setSelected(opt);
    const correct = getTranslation(quizWords[currentIndex]);
    if (!correct) return;
    const isCorrect = opt.toLowerCase() === correct.toLowerCase();
    setFeedback(isCorrect ? 'correct' : 'error');
    setAnswerHistory(prev => [...prev, isCorrect]);
    if (isCorrect) {
      setCorrectCount(c => c + 1);
      setCorrectMessage(VOCAB_CORRECT_MESSAGES[Math.floor(Math.random() * VOCAB_CORRECT_MESSAGES.length)]);
      setStreak(p => {
        const next = p + 1;
        setBestStreak(b => Math.max(b, next));
        if (VOCAB_STREAK_MILESTONES[next]) {
          setMilestone(VOCAB_STREAK_MILESTONES[next]);
          setTimeout(() => setMilestone(null), 2600);
        }
        return next;
      });
      setTimeout(moveToNext, 1000);
    } else {
      setStreak(0);
      setMissedItems(prev => [...prev, { question: getOriginal(quizWords[currentIndex]), correctAnswer: correct }]);
      // wrong answers wait for the student to click "Continua"
    }
  };

const handleTypeCheck = (e) => {
    e.preventDefault();
    if (!typedInput.trim() || feedback) return;
    const clean    = typedInput.toLowerCase().trim().replace(/[-']/g, '');
    const correctRaw = getOriginal(quizWords[currentIndex]);
    const accepted  = getAcceptedAnswers(quizWords[currentIndex])
      .map(a => a.toLowerCase().trim().replace(/[-']/g, ''));
    const isCorrect = accepted.includes(clean);
    setFeedback(isCorrect ? 'correct' : 'error');
    setAnswerHistory(prev => [...prev, isCorrect]);
    if (isCorrect) {
      setCorrectCount(c => c + 1);
      setCorrectMessage(VOCAB_CORRECT_MESSAGES[Math.floor(Math.random() * VOCAB_CORRECT_MESSAGES.length)]);
      setStreak(p => {
        const next = p + 1;
        setBestStreak(b => Math.max(b, next));
        if (VOCAB_STREAK_MILESTONES[next]) {
          setMilestone(VOCAB_STREAK_MILESTONES[next]);
          setTimeout(() => setMilestone(null), 2600);
        }
        return next;
      });
      setTimeout(moveToNext, 1000);
    } else {
      setStreak(0);
      setMissedItems(prev => [...prev, { question: getTranslation(quizWords[currentIndex]), correctAnswer: correctRaw }]);
      // wrong answers wait for the student to click "Continua"
    }
  };

  // Not enough words in either pool
  const effectivePool = topicOverride ? topicOverride.words : allWords;
  if (effectivePool.length < 4) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', opacity: 0.75 }}>
        <p style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
          {topicOverride ? 'Not enough words in this topic for a quiz.' : 'The quiz will unlock shortly.'}
        </p>
      </div>
    );
  }

if (!quizActive && !isFinished && !quizMode) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <p style={quizNudgeStyle}>Scegli la modalità</p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
          <button onClick={() => setQuizMode('choice')} style={vocabModeCardStyle}>
            <span style={{ fontSize: '1.4rem' }}>🔤</span>
            <strong style={{ fontSize: '0.85rem', color: DEEP_MOSS }}>Multiple Choice</strong>
            <span style={{ fontSize: '0.7rem', color: '#888', lineHeight: 1.4 }}>See the Italian word, pick the English translation.</span>
          </button>
          <button onClick={() => setQuizMode('typing')} style={vocabModeCardStyle}>
            <span style={{ fontSize: '1.4rem' }}>✍️</span>
            <strong style={{ fontSize: '0.85rem', color: DEEP_MOSS }}>Type it</strong>
            <span style={{ fontSize: '0.7rem', color: '#888', lineHeight: 1.4 }}>See the English meaning, type the Italian word.</span>
          </button>
        </div>
      </div>
    );
  }

  if (!quizActive && !isFinished) {
    const pool = topicOverride ? topicOverride.words : activePool;
    if (pool.length >= 4) {
      buildQuiz(pool);
    }
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', opacity: 0.75 }}>
        <p style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>Loading...</p>
      </div>
    );
  }

  if (isFinished) {
    const pct  = Math.round((correctCount / quizWords.length) * 100);
    const name = firstName || 'Studente';
    const msg  = pct >= 80 ? `Fantastico, ${name}! You've mastered this. Let's keep this momentum!`
               : pct >= 50 ? `Ottimo lavoro, ${name}! Your hard work is showing. Keep going!`
               : `Forza, ${name}! Every mistake is progress. Let's try one more time!`;
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <p style={quizNudgeStyle}>RISULTATO</p>
        <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 24px' }}>
          <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="60" cy="60" r="50" fill="none" stroke="#f0f0f0" strokeWidth="6" />
            <circle cx="60" cy="60" r="50" fill="none"
              stroke={pct >= 80 ? '#606C38' : pct >= 50 ? '#BC6C25' : '#B23A3A'}
              strokeWidth="6"
              strokeDasharray={`${2 * Math.PI * 50}`}
              strokeDashoffset={`${2 * Math.PI * 50 * (1 - pct / 100)}`}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', fontWeight: 300, color: '#283618', lineHeight: 1 }}>{correctCount}</span>
            <span style={{ fontSize: '0.75rem', color: '#BC6C25', letterSpacing: '1px' }}>/ {quizWords.length}</span>
          </div>
        </div>
        
<p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontStyle: 'italic', color: '#3d4a2e', maxWidth: 300, margin: '0 auto 24px' }}>{msg}</p>

        {bestStreak >= 3 && (
          <p style={{ fontSize: '0.7rem', letterSpacing: '1px', color: TERRACOTTA, fontWeight: 700, marginTop: -12, marginBottom: 20 }}>
            🔥 Best streak: {bestStreak} in a row
          </p>
        )}

        {missedItems.length > 0 && (
          <div style={{ textAlign: 'left', maxWidth: 380, margin: '0 auto 28px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 14, padding: '18px 20px' }}>
            <p style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '3px', color: '#B23A3A', fontWeight: 700, marginBottom: 12 }}>
              Da ripassare
            </p>
            {missedItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '8px 0', borderTop: i > 0 ? '1px solid #eee' : 'none' }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '0.9rem', color: '#444' }}>
                  {item.question}
                </span>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#606C38', whiteSpace: 'nowrap' }}>
                  {item.correctAnswer}
                </span>
              </div>
            ))}
          </div>
        )}

<button onClick={() => buildQuiz(topicOverride ? topicOverride.words : activePool)} style={startQuizBtn}>Riprova</button>

        <button
          onClick={() => {
            if (onBackToFlashcards) {
              onBackToFlashcards();
            } else {
              setQuizActive(false);
              setIsFinished(false);
            }
          }}
          style={{ ...startQuizBtn, backgroundColor: 'transparent', color: '#BC6C25', border: '1.5px solid #BC6C25', marginLeft: '12px' }}>
          Torna alle flashcard
        </button>
      </div>
    );
  }

const current       = quizWords[currentIndex];
  const correctAnswer = quizMode === 'typing' ? getOriginal(current) : getTranslation(current);

  return (
<div ref={quizRef} style={{ maxWidth: '500px', margin: '0 auto', padding: '20px 0' }}>
      {/* Instructions */}
{/* Instructions */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid rgba(188,108,37,0.08)', padding: '12px 16px', marginBottom: '20px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {quizMode === 'typing' ? (
          <>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>🇬🇧 You'll see the English meaning</span>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>✍️ Type the Italian word</span>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>⭐ Score as many as you can!</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>🇮🇹 You'll see an Italian word</span>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>🔤 Pick the English translation</span>
            <span style={{ fontSize: '0.72rem', color: '#666' }}>⭐ Score as many as you can!</span>
          </>
        )}
      </div>

{/* Milestone banner */}
      {milestone && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, animation: 'fadeIn 0.3s ease' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(90deg, #BC6C25, #D4A373)', color: '#fff',
            padding: '10px 24px', borderRadius: 24,
            fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.5px',
            fontFamily: "'Montserrat', sans-serif",
            boxShadow: '0 6px 18px rgba(188,108,37,0.35)',
          }}>
            {milestone}
          </span>
        </div>
      )}

      {/* Streak badge */}
      {!milestone && streak >= 2 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(188,108,37,0.1)', color: TERRACOTTA,
            padding: '5px 16px', borderRadius: 20,
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px',
            fontFamily: "'Montserrat', sans-serif",
          }}>
            🔥 {streak} in a row
          </span>
        </div>
      )}

      {/* Progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: '24px', flexWrap: 'wrap' }}>
        {quizWords.map((_, i) => {
          const result = answerHistory[i]; // undefined = not yet answered, true = correct, false = wrong
          const isCurrent = i === currentIndex && result === undefined;
          let bg = '#eee';
          if (result === true) bg = '#606C38';
          else if (result === false) bg = '#B23A3A';
          else if (isCurrent) bg = TERRACOTTA;
          return (
            <span
              key={i}
              style={{
                width: 9, height: 9, borderRadius: '50%',
                backgroundColor: bg,
                opacity: result === undefined && !isCurrent ? 0.5 : 1,
                transform: isCurrent ? 'scale(1.3)' : 'scale(1)',
                transition: 'all 0.3s ease',
              }}
            />
          );
        })}
      </div>

      {/* Question card */}
      <div key={cardKey} style={{ animation: 'fadeIn 0.3s ease', backgroundColor: feedback === 'correct' ? '#f6faf2' : feedback === 'error' ? '#fff9f9' : '#FCF9F2', padding: '32px', borderRadius: '16px', border: '1px solid rgba(188,108,37,0.1)', transition: 'background 0.3s ease' }}>
        <span style={quizNudgeStyle}>{currentIndex + 1} / {quizWords.length}</span>
       <p style={{ fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#3d4a2e', marginBottom: '10px', marginTop: '24px' }}>
          {quizMode === 'typing'
            ? 'Come si dice in italiano:'
            : (topicOverride?.slug === 'numeri' ? 'Che numero è:' : 'Cosa vuol dire in inglese:')}
        </p>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 5vw, 2.4rem)', fontWeight: 400, fontStyle: 'italic', color: '#283618', margin: '0 0 28px' }}>
          "{quizMode === 'typing' ? getTranslation(current) : getOriginal(current)}"
        </h2>
        {current.example_sentence && (
          <p style={{ fontSize: '0.75rem', fontStyle: 'italic', color: '#BC6C25', opacity: 0.7, margin: '0 0 24px' }}>
            "{current.example_sentence}"
          </p>
        )}
        {quizMode === 'typing' ? (
          <form onSubmit={handleTypeCheck} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <input
              value={typedInput}
              onChange={e => setTypedInput(e.target.value)}
              placeholder="scrivi in italiano..."
              autoFocus autoComplete="off" disabled={!!feedback}
              style={{
                width: '100%', maxWidth: 300, background: 'transparent', border: 'none',
                borderBottom: `1.5px solid ${feedback === 'correct' ? '#606C38' : feedback === 'error' ? '#B23A3A' : '#e0e0e0'}`,
                padding: '10px 0', fontSize: '1.25rem', fontWeight: 600,
                textAlign: 'center', outline: 'none', color: '#283618',
                fontFamily: "'Montserrat', sans-serif", transition: 'border-color 0.3s ease',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button type="submit" disabled={!!feedback} style={{ background: 'none', border: `1.5px solid ${TERRACOTTA}`, color: TERRACOTTA, padding: '9px 28px', borderRadius: 50, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '2px', cursor: feedback ? 'default' : 'pointer', fontWeight: 700, fontFamily: "'Montserrat', sans-serif", opacity: feedback ? 0.4 : 1 }}>
                Controlla
              </button>
              {!feedback && !showHint && (
                <button type="button" onClick={() => setShowHint(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TERRACOTTA, fontSize: '0.65rem', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'underline', fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>
                  suggerimento
                </button>
              )}
              {showHint && (
                <span style={{ fontSize: '0.75rem', color: TERRACOTTA }}>
                  inizia con <strong style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>{correctAnswer.slice(0, 3)}…</strong>
                </span>
              )}
            </div>
          </form>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {options.map((opt, i) => {
              const isSelected = selected === opt;
              const isRight    = opt.toLowerCase() === correctAnswer.toLowerCase();
              let bg = '#fff', border = '1.5px solid #eee', color = '#283618';
              if (feedback) {
                if (isSelected && feedback === 'correct') { bg = '#f0f7ec'; border = '1.5px solid #606C38'; color = '#606C38'; }
                else if (isSelected && feedback === 'error') { bg = '#fff5f5'; border = '1.5px solid #B23A3A'; color = '#B23A3A'; }
                else if (!isSelected && isRight && feedback === 'error') { bg = '#f0f7ec'; border = '1.5px solid #606C38'; color = '#606C38'; }
              }
              return (
                <button key={i} disabled={!!feedback} onClick={() => handleSelect(opt)}
                  style={{ backgroundColor: bg, border, color, padding: '14px 12px', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: feedback ? 'default' : 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s ease' }}>
                  {opt}
                </button>
              );
            })}
          </div>
        )}

<div style={{ minHeight: '52px', marginTop: '20px', textAlign: 'center' }}>
          {feedback === 'correct' && <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', fontStyle: 'italic', color: '#606C38', margin: 0 }}>{correctMessage}</p>}
          {feedback === 'error' && (
            <div>
              <p style={{ fontSize: '0.85rem', color: '#B23A3A', margin: '0 0 14px' }}>La risposta è <strong>"{correctAnswer}"</strong></p>
              <button
                onClick={moveToNext}
                style={{
                  background: '#B23A3A', color: '#fff', border: 'none',
                  padding: '9px 28px', borderRadius: 50, fontSize: '0.68rem', fontWeight: 700,
                  letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Continua →
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ─── Dizionario Parole ────────────────────────────────────────────────────────
const DizionarioParole = ({ allWords }) => {
  const [search, setSearch] = useState('');

  const filtered = allWords
    .slice()
    .sort((a, b) => a.word_original.localeCompare(b.word_original))
    .filter(w =>
      w.word_original.toLowerCase().includes(search.toLowerCase()) ||
      w.word_translation.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: '16px', padding: '28px 24px', marginBottom: '36px', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
      <span style={sharedKicker}>Dizionario</span>
      <input
        placeholder="Cerca una parola..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
        style={sharedDictSearch}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: '14px', maxHeight: '280px', overflowY: 'auto', paddingRight: '6px' }}>
        {filtered.length === 0
          ? <p style={{ textAlign: 'center', opacity: 0.7, fontSize: '0.8rem', padding: '20px', gridColumn: '1/-1' }}>Nessun risultato.</p>
          : filtered.map((w, i) => (
            <div key={w.id || i} style={{ fontSize: '0.82rem', padding: '10px 12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f5f5f5' }}>
              <div style={{ fontWeight: 600, color: '#283618', marginBottom: '5px' }}>
                {w.word_original.toUpperCase()}
                <span style={{ fontWeight: 300, opacity: 0.75 }}> · {w.word_translation}</span>
              </div>
              {w.example_sentence && (
                <span style={{ fontSize: '0.75rem', background: '#fff', border: '1px solid #eee', padding: '2px 8px', borderRadius: '20px', color: '#3d4a2e', display: 'inline-block', marginTop: '4px', fontStyle: 'italic' }}>
                  "{w.example_sentence}"
                </span>
              )}
            </div>
          ))
        }
      </div>
    </div>
  );
};

// ─── Pay Button ──────────────────────────────────────────────────────────────
const VENMO_URL  = 'https://venmo.com/francescamuntoni';
const WISE_URL = 'https://wise.com/pay/me/francescam856';

const PayButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={studentPayBtnStyle}
      >
        💳 Pay now {open ? '▲' : '▼'}
      </button>

      {open && (
        <div style={payDropdownStyle}>
          <a
            href={WISE_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            style={payOptionStyle}
          >
            <span style={{ fontSize: '1rem' }}>🌐</span>
            <div>
              <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 700, color: DEEP_MOSS }}>Wise</p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#3d4a2e' }}>International & US</p>
            </div>
          </a>
          <div style={{ height: '1px', backgroundColor: '#f0f0f0', margin: '4px 0' }} />
          <a
            href={VENMO_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            style={payOptionStyle}
          >
            <span style={{ fontSize: '1rem' }}>🇺🇸</span>
            <div>
              <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 700, color: DEEP_MOSS }}>Venmo</p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#3d4a2e' }}>US only</p>
            </div>
          </a>
        </div>
      )}
    </div>
  );
};


// ─── Topic Flashcard Deck ─────────────────────────────────────────────────────
const TopicDeck = ({ topic, onClose, onQuiz }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [browseMode, setBrowseMode] = useState(false);
  const words = topic.words;
  const word  = words[currentIndex];
  const cardRef = useRef(null);

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);


  return (
    <div style={{ animation: 'fadeIn 0.3s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.6rem' }}>{topic.emoji}</span>
          <div>
            <p style={{ margin: 0, fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, fontWeight: 700 }}>
              {TOPIC_SYLLABUS_MAP[topic.slug]?.level || 'Extra Vocabulary'}
            </p>
            <h3 style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', fontStyle: 'italic', color: DEEP_MOSS, lineHeight: 1.2 }}>
              {topic.name}
            </h3>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button onClick={onClose}
  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', border: `1.5px solid ${TERRACOTTA}`, backgroundColor: '#fff', color: TERRACOTTA, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap' }}>
  ← Change topic
</button>
</div>
      </div>

{/* Progress bar */}
<div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
  <div style={{ flex: 1, height: '3px', backgroundColor: '#f0f0f0', borderRadius: '10px', overflow: 'hidden' }}>
    <div style={{ height: '100%', backgroundColor: TERRACOTTA, borderRadius: '10px', width: `${((currentIndex + 1) / words.length) * 100}%`, transition: 'width 0.3s ease' }} />
  </div>
  <span style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 700, whiteSpace: 'nowrap' }}>{currentIndex + 1} / {words.length}</span>
</div>

{/* Quiz nudge banner */}
<button onClick={() => onQuiz(topic)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', marginBottom: '20px', backgroundColor: 'rgba(188,108,37,0.06)', border: '1px dashed rgba(188,108,37,0.3)', borderRadius: '10px', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
  <span style={{ fontSize: '0.72rem', color: TERRACOTTA, fontWeight: 600 }}>
    🧠 Ready to test yourself?
  </span>
  <span style={{ fontSize: '0.68rem', color: TERRACOTTA, fontWeight: 700, letterSpacing: '0.5px' }}>
    Take the quiz →
  </span>
</button>

{/* Browse mode — compact grid */}
{browseMode ? (
  <div>
    <button onClick={() => setBrowseMode(false)}
      style={{ ...navBtnStyle, marginBottom: '16px', fontSize: '0.7rem' }}>← Back to cards</button>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginTop: '8px' }}>
      {words.map((w, i) => {
        const italian = w.word_original || w.italian || '';
        const english = w.word_translation || w.english || '';
        return (
          <div key={i} style={{ backgroundColor: '#fff', borderRadius: '10px', border: '1px solid rgba(188,108,37,0.1)', padding: '12px', display: 'flex', flexDirection: 'column', gap: '4px', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
            {w.emoji && <span style={{ fontSize: '1.4rem' }}>{w.emoji}</span>}
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.3 }}>{italian}</span>
            <span style={{ fontSize: '0.72rem', color: TERRACOTTA, lineHeight: 1.3 }}>{english}</span>
          </div>
        );
      })}
    </div>
  </div>
) : (
        <>
          {/* Card */}
          <div ref={cardRef} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
  <Flashcard key={currentIndex} word={word} />
</div>
          {/* Navigation */}
          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
            <button onClick={() => setCurrentIndex(i => Math.max(0, i - 1))} disabled={currentIndex === 0}
              style={{ ...navBtnStyle, opacity: currentIndex === 0 ? 0.3 : 1 }}>← Prev</button>
            <button onClick={() => setCurrentIndex(i => Math.min(words.length - 1, i + 1))} disabled={currentIndex === words.length - 1}
              style={{ ...navBtnStyle, opacity: currentIndex === words.length - 1 ? 0.3 : 1 }}>Next →</button>
            <button onClick={() => setBrowseMode(true)}
              style={{ ...navBtnStyle, fontSize: '0.7rem' }}>☰ All words</button>
          </div>

          {/* Finished */}
          {currentIndex === words.length - 1 && (
            <div style={{ textAlign: 'center', marginTop: '28px', padding: '24px', backgroundColor: '#fdf8f4', borderRadius: '16px', border: '1px solid rgba(188,108,37,0.1)' }}>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.3rem', fontStyle: 'italic', color: TERRACOTTA, margin: '0 0 16px' }}>
                Ottimo! All {words.length} cards done 🎉
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setCurrentIndex(0)}
                  style={{ ...navBtnStyle, padding: '10px 24px', fontSize: '0.72rem' }}>↺ Start over</button>
                <button onClick={() => onQuiz(topic)}
                  style={{ padding: '10px 24px', borderRadius: '50px', border: 'none', backgroundColor: TERRACOTTA, color: '#fff', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}>
                  🧠 Quiz me now
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Vocabolario Tab ──────────────────────────────────────────────────────────
const VocabolarioTab = ({ newWords, allWords, firstName }) => {
  const [activeSection,  setActiveSection]  = useState('flashcards'); // 'flashcards' | 'quiz'
  const [activeTopic,    setActiveTopic]    = useState(null);
  const [quizTopic,      setQuizTopic]      = useState(null);
  const [dictOpen,       setDictOpen]       = useState(false);
  const [openModules, setOpenModules] = useState({});
  const topicGridRef = useRef(null);

  const handleQuiz = (topic) => {
    setQuizTopic(topic);
    setActiveSection('quiz');
    setActiveTopic(null);
  };

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentWords = allWords.filter(w => new Date(w.lessonDate) >= thirtyDaysAgo);

  // Group topics by syllabus level
  const getTopicsForLevel = (levelName) =>
    TOPICS.filter(t => TOPIC_SYLLABUS_MAP[t.slug]?.level === levelName);

  const extraTopics = TOPICS.filter(t => !TOPIC_SYLLABUS_MAP[t.slug]);

  const toggleModule = (moduleKey) =>
    setOpenModules(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }));

  return (
    <section style={{ animation: 'fadeIn 0.5s ease' }}>

{/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px', paddingTop: '20px' }}>
<p style={{ color: TERRACOTTA, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600, marginBottom: 8 }}>
          LA TUA VOCE
        </p>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.6rem, 5vw, 2.2rem)', lineHeight: 1.2, color: DEEP_MOSS, margin: '0 0 6px', fontWeight: 700 }}>
          Building your own voice
          <br />
          <span style={{ fontStyle: 'italic', color: TERRACOTTA, fontWeight: 400 }}>
            one <strong>word</strong> at a time
          </span>
        </h3>
        <div style={{ width: 40, height: 1, backgroundColor: TERRACOTTA, margin: '16px auto 0', opacity: 0.75 }} />
      </div>

      {/* Two-tab toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
        <div style={{ display: 'flex', backgroundColor: '#f5f5f5', borderRadius: '12px', padding: '4px', gap: '2px' }}>
          {[
            { id: 'flashcards', label: '🃏 Flashcards' },
            { id: 'quiz',       label: '🧠 Quiz' },
          ].map(s => (
            <button key={s.id}
              onClick={() => { setActiveSection(s.id); setActiveTopic(null); if (s.id === 'flashcards') setQuizTopic(null); }}
              style={{ padding: '10px 28px', borderRadius: '9px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', whiteSpace: 'nowrap', transition: 'all 0.2s ease', backgroundColor: activeSection === s.id ? TERRACOTTA : 'transparent', color: activeSection === s.id ? '#fff' : '#888' }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── FLASHCARDS ── */}
      {activeSection === 'flashcards' && (
        activeTopic ? (
<TopicDeck topic={activeTopic} onClose={() => {
  setActiveTopic(null);
  setTimeout(() => {
    if (topicGridRef.current) {
      topicGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, 50);
}} onQuiz={handleQuiz} />
        ) : (
          <div>
  <div ref={topicGridRef} />

            {/* Syllabus sections */}
            {SYLLABUS_SECTIONS.map(({ module, moduleLabel, color, levels }) => {
              const isOpen = openModules[module];
              const totalTopics = levels.reduce((acc, l) => acc + getTopicsForLevel(l.level).length, 0);
              return (
                <div key={module} style={{ marginBottom: '16px' }}>
                  {/* Module header — collapsible */}
                  <button
                    onClick={() => toggleModule(module)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', backgroundColor: '#fff', borderRadius: isOpen ? '14px 14px 0 0' : '14px', border: `1px solid rgba(0,0,0,0.06)`, borderLeft: `4px solid ${color}`, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", textAlign: 'left', transition: 'all 0.2s ease' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color }}>{moduleLabel}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#888' }}>{totalTopics} topics</p>
                      </div>
                    </div>
                    <span style={{ color, fontSize: '0.8rem', fontWeight: 700 }}>{isOpen ? '▲' : '▼'}</span>
                  </button>

                  {/* Levels inside module */}
                  {isOpen && (
                    <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderTop: 'none', borderRadius: '0 0 14px 14px', overflow: 'hidden' }}>
                      {levels.map(({ level, focus }, lIdx) => {

const levelTopics = getTopicsForLevel(level);
return (
  <div key={level} style={{ padding: '20px 18px', borderTop: lIdx === 0 ? 'none' : '1px solid #f5f5f5', backgroundColor: '#fafafa' }}>
    {levelTopics.length === 0 && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
        <span style={{ fontSize: '0.72rem', letterSpacing: '2px', textTransform: 'uppercase', color, fontWeight: 700 }}>{level}</span>
        <span style={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic' }}>— coming soon</span>
      </div>
    )}
    {levelTopics.length > 0 && (
      <div>
        {/* Level label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
          <span style={{ fontSize: '0.72rem', letterSpacing: '2px', textTransform: 'uppercase', color, fontWeight: 700 }}>{level}</span>
        </div>
        {/* Topic cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
          {levelTopics.map(topic => (
                                <button key={topic.slug} onClick={() => setActiveTopic(topic)}
                                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', padding: '12px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid rgba(188,108,37,0.1)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease', fontFamily: "'Montserrat', sans-serif", boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.08)`; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(188,108,37,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.03)'; }}>
                                  <span style={{ fontSize: '1.3rem' }}>{topic.emoji}</span>
                                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.3 }}>{topic.name}</span>
                                  <span style={{ fontSize: '0.6rem', color: '#bbb' }}>{topic.words.length} words</span>
                                </button>
                              ))}
                            </div>
                            </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Extra Vocabulary */}
            {extraTopics.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <button
                  onClick={() => toggleModule('extra')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', backgroundColor: '#fff', borderRadius: openModules['extra'] ? '14px 14px 0 0' : '14px', border: '1px solid rgba(0,0,0,0.06)', borderLeft: '4px solid #9b8ea0', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", textAlign: 'left' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#9b8ea0' }}>📚 Extra Vocabulary</p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#888' }}>Some useful words · {extraTopics.length} topics</p>
                  </div>
                  <span style={{ color: '#9b8ea0', fontSize: '0.8rem', fontWeight: 700 }}>{openModules['extra'] ? '▲' : '▼'}</span>
                </button>
                {openModules['extra'] && (
                  <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderTop: 'none', borderRadius: '0 0 14px 14px', padding: '20px 18px', backgroundColor: '#fafafa' }}>
                    <p style={{ margin: '0 0 14px', fontSize: '0.72rem', color: '#aaa', fontStyle: 'italic' }}>
                      Fun vocabulary from "Le Prime Mille Parole" — great for building visual memory!
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                      {extraTopics.map(topic => (
                        <button key={topic.slug} onClick={() => setActiveTopic(topic)}
                          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', padding: '12px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid rgba(155,142,160,0.2)', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease', fontFamily: "'Montserrat', sans-serif" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#9b8ea0'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(155,142,160,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <span style={{ fontSize: '1.3rem' }}>{topic.emoji}</span>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.3 }}>{topic.name}</span>
                          <span style={{ fontSize: '0.6rem', color: '#bbb' }}>{topic.words.length} words</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* My Lesson Words divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '8px 0 20px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#f0f0f0' }} />
              <span style={{ fontSize: '0.68rem', letterSpacing: '3px', color: TERRACOTTA, textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>My Lesson Words</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#f0f0f0' }} />
            </div>

            {/* My Lesson Words explanation */}
            <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid rgba(188,108,37,0.1)', padding: '16px 20px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '0.72rem', fontWeight: 700, color: TERRACOTTA, textTransform: 'uppercase', letterSpacing: '1px' }}>
                📝 Words Francesca added during your lessons
              </p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#888', lineHeight: 1.6 }}>
                These are words that came up specifically in <em>your</em> sessions. They appear below as flashcards.
              </p>
            </div>

            {/* Dictionary toggle */}
            {allWords.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <button onClick={() => setDictOpen(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 20px', borderRadius: '50px', fontSize: '0.72rem', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.2s ease', background: dictOpen ? DEEP_MOSS : '#fcfcfc', color: dictOpen ? '#fff' : DEEP_MOSS, border: dictOpen ? `1.5px solid ${DEEP_MOSS}` : '1.5px solid #eee' }}>
                  <span>{dictOpen ? '✕' : '📖'}</span>
                  {dictOpen ? 'Chiudi dizionario' : `Sfoglia tutti (${allWords.length})`}
                </button>
              </div>
            )}
            {dictOpen && <DizionarioParole allWords={allWords} />}

            {/* Recent flashcards */}
            {recentWords.length > 0 ? (
              <div style={vocabGrid}>
                {recentWords.map((v, i) => <Flashcard key={`card-${v.id || i}`} word={v} />)}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px 20px', opacity: 0.6 }}>
                <p style={{ fontSize: '0.85rem', fontStyle: 'italic', color: '#666', margin: 0 }}>
                  No words added in the last 30 days. They'll appear here after your next lesson!
                </p>
              </div>
            )}
          </div>
        )
      )}

      {/* ── QUIZ ── */}
      {activeSection === 'quiz' && (
        <div>
          {!quizTopic ? (
            <div style={{ marginBottom: '24px' }}>
              {/* Quiz topic selector — same syllabus structure */}
              <p style={{ fontSize: '0.72rem', letterSpacing: '2px', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: '20px', textAlign: 'center' }}>
                Choose a topic to quiz
              </p>

              {/* My Lesson Words — at the top */}
              {allWords.length >= 4 && (
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, borderLeft: `3px solid ${TERRACOTTA}`, paddingLeft: '10px' }}>📝 My Lesson Words</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                    <button
                      onClick={() => setQuizTopic({ name: 'All lesson words', emoji: '📖', slug: 'personal-all', words: allWords.map(w => ({ italian: w.word_original, english: w.word_translation, example_sentence: w.example_sentence })) })}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#fff', borderRadius: '10px', border: `1px solid rgba(188,108,37,0.2)`, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s ease', textAlign: 'left' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = TERRACOTTA; e.currentTarget.style.backgroundColor = 'rgba(188,108,37,0.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(188,108,37,0.2)'; e.currentTarget.style.backgroundColor = '#fff'; }}>
                      <span style={{ fontSize: '1.1rem' }}>📖</span>
                      <div>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.2, display: 'block' }}>All lesson words</span>
                        <span style={{ fontSize: '0.6rem', color: '#bbb' }}>{allWords.length} words</span>
                      </div>
                    </button>
                    {recentWords.length >= 4 && (
                      <button
                        onClick={() => setQuizTopic({ name: 'Last 30 days', emoji: '🗓', slug: 'personal-recent', words: recentWords.map(w => ({ italian: w.word_original, english: w.word_translation, example_sentence: w.example_sentence })) })}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#fff', borderRadius: '10px', border: `1px solid rgba(188,108,37,0.2)`, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s ease', textAlign: 'left' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = TERRACOTTA; e.currentTarget.style.backgroundColor = 'rgba(188,108,37,0.03)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(188,108,37,0.2)'; e.currentTarget.style.backgroundColor = '#fff'; }}>
                        <span style={{ fontSize: '1.1rem' }}>🗓</span>
                        <div>
                          <span style={{ fontSize: '0.67rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.2, display: 'block' }}>Last 30 days</span>
                          <span style={{ fontSize: '0.6rem', color: '#bbb' }}>{recentWords.length} words</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {SYLLABUS_SECTIONS.map(({ module, moduleLabel, color, levels }) => (
                <div key={module} style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color, borderLeft: `3px solid ${color}`, paddingLeft: '10px' }}>{moduleLabel}</p>
                  {levels.map(({ level }) => {
                    const levelTopics = getTopicsForLevel(level);
                    return (
                      <div key={level} style={{ marginBottom: '12px' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '0.65rem', color: '#aaa', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600 }}>{level}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                          {levelTopics.map(topic => (
                            <button key={topic.slug} onClick={() => setQuizTopic(topic)}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid rgba(188,108,37,0.1)', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s ease', textAlign: 'left' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.backgroundColor = 'rgba(188,108,37,0.03)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(188,108,37,0.1)'; e.currentTarget.style.backgroundColor = '#fff'; }}>
                              <span style={{ fontSize: '1.1rem' }}>{topic.emoji}</span>
                              <span style={{ fontSize: '0.67rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.2 }}>{topic.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Extra topics in quiz */}
              {extraTopics.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#9b8ea0', borderLeft: '3px solid #9b8ea0', paddingLeft: '10px' }}>📚 Extra Vocabulary</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '6px' }}>
                    {extraTopics.map(topic => (
                      <button key={topic.slug} onClick={() => setQuizTopic(topic)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid rgba(155,142,160,0.2)', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.15s ease', textAlign: 'left' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#9b8ea0'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(155,142,160,0.2)'; }}>
                        <span style={{ fontSize: '1.1rem' }}>{topic.emoji}</span>
                        <span style={{ fontSize: '0.67rem', fontWeight: 700, color: DEEP_MOSS, lineHeight: 1.2 }}>{topic.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>
       ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: '12px 16px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid rgba(188,108,37,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.2rem' }}>{quizTopic?.emoji || '📝'}</span>
            <div>
              <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700, color: DEEP_MOSS }}>{quizTopic?.name}</p>
              <p style={{ margin: 0, fontSize: '0.65rem', color: '#aaa' }}>{quizTopic?.words?.length} words</p>
            </div>
          </div>
          <button
            onClick={() => {
              setQuizTopic(null);
              setTimeout(() => {
                if (topicGridRef.current) {
                  topicGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 50);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', border: `1.5px solid ${TERRACOTTA}`, backgroundColor: '#fff', color: TERRACOTTA, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap' }}>
            ← Change topic
          </button>
        </div>
      )}

{quizTopic && (
  <VocabQuiz
    key={quizTopic.slug}
    allWords={allWords}
    firstName={firstName}
    topicOverride={quizTopic}
    onBackToFlashcards={() => {
      setActiveSection('flashcards');
      setQuizTopic(null);
      setActiveTopic(null);
    }}
  />
)}</div>
      )}
    </section>
  );
};


// ─── Main Component ───────────────────────────────────────────────────────────
const StudentDashboard = ({ user }) => {
  const isMobile = window.innerWidth < 768;
  const [showPassword,     setShowPassword]     = useState(false);
  const [lessons,          setLessons]          = useState([]);
  const [resources,        setResources]        = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [firstName,        setFirstName]        = useState('');
  const [activeTab,        setActiveTab]        = useState('dashboard');
  const [meetingLink,      setMeetingLink]      = useState(null);
  const [email,            setEmail]            = useState('');
  const [password,         setPassword]         = useState('');
  const [loginError,       setLoginError]       = useState(null);
  const [masteryData,      setMasteryData]      = useState([]);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [reschedulingLesson, setReschedulingLesson] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [requestedDate,    setRequestedDate]    = useState(null);
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);
  const [scrollToBooking,      setScrollToBooking]      = useState(false);
  const [scrollToLessonId,     setScrollToLessonId]     = useState(null);
  const [showIdleWarning,      setShowIdleWarning]      = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [reviewModalOpen,   setReviewModalOpen]   = useState(false);
  const [reviewSubmitted,   setReviewSubmitted]   = useState(false);
  const [reviewForm,        setReviewForm]        = useState({ location: '', tag: '', text: '' });
  const [reviewSubmitting,  setReviewSubmitting]  = useState(false);
  const [reviewSuccess,     setReviewSuccess]     = useState(false);
  const loginPageWrapper = { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? '65vh' : '75vh', padding: '20px', backgroundColor: '#fcfcfc', boxSizing: 'border-box' };
  const loginFormContainer       = { maxWidth: '400px', margin: '0 auto', backgroundColor: '#fff', padding: isMobile ? '24px' : '40px', borderRadius: '20px', boxShadow: '0 15px 35px rgba(40, 54, 24, 0.05)', border: '1px solid rgba(188, 108, 37, 0.1)', textAlign: 'left', transform: isMobile ? 'translateY(-20px)' : 'none' };
  


  // ── Auto-logout after 30 min inactivity ──────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const TIMEOUT_MS = 30 * 60 * 1000;  // 30 minutes
    const WARNING_MS = 29 * 60 * 1000;  // warn at 29 minutes
    let warnTimer, logoutTimer;

    const reset = () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      setShowIdleWarning(false);
      warnTimer   = setTimeout(() => setShowIdleWarning(true), WARNING_MS);
      logoutTimer = setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.reload();
      }, TIMEOUT_MS);
    };

    const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user]); // eslint-disable-line

  const location = useLocation();
  const navigate  = useNavigate();

useEffect(() => {
  const params = new URLSearchParams(location.search);
  if (params.get('reset') === '1') {
    setActiveTab('dashboard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/portal', { replace: true });
  }
  if (params.get('booking') === '1') {
    setActiveTab('dashboard');
    setScrollToBooking(true);
    navigate('/portal', { replace: true });
  }
  const tab = params.get('tab');
  const lessonId = params.get('lessonId');
  if (tab) {
    setActiveTab(tab);
    if (lessonId) {
      setScrollToLessonId(lessonId);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    navigate('/portal', { replace: true });
  }
}, [location.search, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollToBooking && activeTab === 'dashboard') {
      // Use requestAnimationFrame to wait until after React has painted the tab
      const raf = requestAnimationFrame(() => {
        const el = document.getElementById('booking');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
        setScrollToBooking(false);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [scrollToBooking, activeTab]);

useEffect(() => {
  if (scrollToLessonId && activeTab === 'dashboard') {
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`lesson-${scrollToLessonId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollToLessonId(null);
    });
    return () => cancelAnimationFrame(raf);
  }
}, [scrollToLessonId, activeTab]);

  // ── Escape key closes modals ─────────────────────────────────────────────
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (showIdleWarning) setShowIdleWarning(false);
      if (isRescheduleOpen) { setIsRescheduleOpen(false); setReschedulingLesson(null); setRequestedDate(null); setRescheduleReason(''); }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showIdleWarning, isRescheduleOpen]); // eslint-disable-line

  const isLocked = (lessonDate) => {
    const diff = (new Date(lessonDate) - new Date()) / (1000 * 60 * 60);
    return diff < 24 && diff > 0;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5  && hour < 12) return 'Buongiorno';
    if (hour >= 12 && hour < 18) return 'Buon pomeriggio';
    if (hour >= 18 && hour < 23) return 'Buonasera';
    return 'Ciao'; // 11pm–5am
  };

  useEffect(() => {
    if (user?.user_metadata?.full_name) {
      const full = user.user_metadata.full_name;
      const raw = full.toLowerCase().includes(' e ') ? full : full.split(' ')[0];
      setFirstName(raw.charAt(0).toUpperCase() + raw.slice(1));
    } else if (user?.email) {
      const prefix = user.email.split('@')[0];
      setFirstName(prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase());
    }
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
  }, [user]);

  const fetchStudentLessons = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase.from('lessons').select('*, vocabulary (*)').eq('user_id', userId).order('created_at', { ascending: false });
      if (error) throw error;
      setLessons(data || []);
    } catch (error) { console.error('Errore lezioni:', error.message); }
  }, []);

  const fetchResources = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase.from('resources').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      if (error) throw error;
      setResources(data || []);
    } catch (error) { console.error('Errore risorse:', error.message); }
  }, []);
  
  useEffect(() => {
    const loadAllData = async () => {
      if (!user?.id) { setLoading(false); return; }
      setLoading(true);
      const { data: profileData } = await supabase.from('profiles').select('meeting_link, review_submitted').eq('id', user.id).single();
if (profileData?.meeting_link)     setMeetingLink(profileData.meeting_link);
if (profileData?.review_submitted) setReviewSubmitted(true);
      const { data: mastery } = await supabase.from('student_mastery').select('topic_name').eq('student_id', user.id);
      setMasteryData(mastery || []);
      await Promise.all([fetchStudentLessons(user.id), fetchResources(user.id)]);
      setLoading(false);
    };
    loadAllData();
  }, [user?.id, fetchStudentLessons, fetchResources]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message?.toLowerCase().includes('invalid') || error.message?.toLowerCase().includes('credentials') || error.message?.toLowerCase().includes('password')) {
        setLoginError('Wrong email or password. Please try again.');
      } else if (error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch') || error.status === 0) {
        setLoginError('Connection error. Please check your internet and try again.');
      } else if (error.status >= 500) {
        setLoginError('Our servers are having a moment. Please try again in a few seconds.');
      } else {
        setLoginError('Something went wrong. Please try again.');
      }
    }
  };
const handleReviewSubmit = async (e) => {
  e.preventDefault();
  if (!reviewForm.text.trim()) return;
  setReviewSubmitting(true);
  try {
    const fullName = user.user_metadata?.full_name || firstName;
    const message  = `[REVIEW] name: "${fullName}" · location: "${reviewForm.location}" · tag: "${reviewForm.tag}" · text: "${reviewForm.text.trim()}"`;
    await supabase.from('notifications').insert([{
      user_id:      user.id,
      student_name: fullName,
      message,
      is_read:      false,
    }]);
const { error: profileUpdateError } = await supabase.rpc('mark_review_submitted');
if (profileUpdateError) console.error('Profile update failed:', profileUpdateError);


    setReviewSubmitted(true);
    setReviewModalOpen(false);
    setReviewSuccess(true);
  } catch (err) {
    console.error('Review error:', err);
  } finally {
    setReviewSubmitting(false);
  }
};
  const handleEmailSubmit = () => {
    const subject = encodeURIComponent(`Homework Submission - ${firstName || 'Student'}`);
    const body    = encodeURIComponent("Ciao Francesca! I've attached my homework for our latest session.");
    window.location.href = `mailto:${MAESTRA_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleRescheduleSubmit = async (lessonId) => {
    const targetLesson = reschedulingLesson;
    if (!targetLesson) return;
    if (!requestedDate?.date || !requestedDate?.hour || !requestedDate?.minute) return alert('Please select a date and time.');
    setIsSubmitting(true);

    const newDateTime  = new Date(`${requestedDate.date}T${requestedDate.hour}:${requestedDate.minute}:00`);
    const formattedNew = newDateTime.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
                       + ` alle ${requestedDate.hour}:${requestedDate.minute}`;
    const formattedOld = new Date(targetLesson.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });

    try {
      const { error } = await supabase.from('notifications').insert([{
        user_id:        user.id,
        student_name:   user.user_metadata?.full_name || firstName,
        message:        `[RESCHEDULE REQ] Spostare la lezione del ${formattedOld} → ${formattedNew}.${rescheduleReason ? ' Motivo: ' + rescheduleReason : ''}`,
        lesson_id:      lessonId,
        requested_time: newDateTime.toISOString(),
        is_read:        false,
      }]);
      if (error) throw error;
      setRescheduleSuccess(true);
      setReschedulingLesson(null);
      setRequestedDate(null);
      setRescheduleReason('');
    } catch (err) {
      console.error('Error:', err);
      alert('Error sending request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const { newWords, allWords } = useMemo(() => {
    if (!lessons.length) return { newWords: [], allWords: [] };
    const lessonWithWords   = lessons.find(l => l.vocabulary?.length > 0);
    const latestLessonWords = lessonWithWords?.vocabulary || [];
    const seen              = new Set();
    const uniqueAll         = lessons.flatMap(l =>
      (l.vocabulary || []).map(v => ({ ...v, lessonDate: l.created_at }))
    ).filter(item => {
      const val = item.word_original.toLowerCase();
      if (seen.has(val)) return false;
      seen.add(val);
      return true;
    });
    return { newWords: latestLessonWords, allWords: uniqueAll };
  }, [lessons]);

  const { progressPercentage, masteredSet, completedCount } = useMemo(() => {
    const mastered  = new Set(masteryData.map(m => m.topic_name));
    const allTopics = (SYLLABUS || []).flatMap(m => (m.subLevels || []).flatMap(s => s.topics || []));
    const completed = allTopics.filter(t => mastered.has(t));
    return {
      masteredSet:        mastered,
      completedCount:     completed.length,
      progressPercentage: allTopics.length > 0 ? Math.min(Math.round((completed.length / allTopics.length) * 100), 100) : 0,
    };
  }, [masteryData]);

  const now            = new Date();
  const sorted         = [...lessons].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const lessonEndBuffer = new Date(now.getTime() - 65 * 60 * 1000); // 65 min grace period
  const upcoming = sorted.filter(l => new Date(l.created_at) >= lessonEndBuffer);
  const past     = sorted.filter(l => new Date(l.created_at) < lessonEndBuffer).reverse();
  const featuredLesson = upcoming[0] || null;
  const nextLessons    = upcoming.slice(1);
  const archiveLessons = past;
  const unpaidCount    = archiveLessons.filter(l => !l.paid).length;

  const isConfirmedNote = (notes) =>
    notes?.includes('[CONFIRMED]') ||
    notes?.toLowerCase().includes('updated') ||
    notes?.toLowerCase().includes('confirmed');

  const NAV_TABS = [
    { id: 'dashboard',   label: '🏠 Dashboard' },
    { id: 'roadmap',     label: '📍 Roadmap' },
    { id: 'resources',   label: `📚 Risorse (${resources.length})` },
    { id: 'vocabolario', label: `📇 Parole` },
    { id: 'verbi',       label: '🇮🇹 Verbi' },
  ];

  // ── Login ─────────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div style={loginPageWrapper}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <header style={headerStyle}>
            <p style={kickerStyle}>Student Portal</p>
            <h2 style={{ ...titleStyle, marginBottom: '40px', fontSize: '2rem' }}>Login</h2>
          </header>
          
          <form onSubmit={handleLogin} style={{ ...loginFormContainer, background: 'linear-gradient(to right, rgba(0,146,70,0.13) 0%, rgba(0,146,70,0.13) 33.3%, rgba(255,255,255,0) 33.3%, rgba(255,255,255,0) 66.6%, rgba(206,43,55,0.13) 66.6%, rgba(206,43,55,0.13) 100%), #fff' }} aria-label="Student login">            <div style={inputGroup}>
              <label htmlFor="login-email" style={labelStyle}>Email</label>
              <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} placeholder="your@email.com" required />
            </div>
            <div style={inputGroup}>
              <label htmlFor="login-password" style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, paddingRight: '45px' }} placeholder="••••••••••••" required />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={togglePasswordButtonStyle} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? 'Hide' : 'Show'}</button>
              </div>
            </div>
            {loginError && <p style={errorTextStyle}>{loginError}</p>}
            <button type="submit" style={loginButtonStyle}>Enter the Portal</button>
            <p style={{ textAlign: 'center', marginTop: '16px', margin: '16px 0 0' }}>
              <a
                href="https://sayciao.school/reset-password"
                onClick={async (e) => {
                  e.preventDefault();
                  const emailVal = email.trim();
                  if (!emailVal) { setLoginError('Enter your email address first, then click Forgot password.'); return; }
                  const { error } = await supabase.auth.resetPasswordForEmail(emailVal, { redirectTo: `${window.location.origin}/reset-password` });
                  if (error) { setLoginError('Could not send reset email. Please try again.'); }
                  else { setLoginError(null); setResetSent(true); }
                }}
                style={{ fontSize: '0.75rem', color: '#3d4a2e', textDecoration: 'none', borderBottom: '1px solid #ccc', paddingBottom: '1px', fontFamily: "'Montserrat', sans-serif" }}
              >
                Forgot your password?
              </a>
            </p>
            {resetSent && (
              <div style={{ marginTop: '16px', padding: '14px 16px', backgroundColor: '#f0f7ec', borderRadius: '10px', border: '1px solid rgba(96,108,56,0.25)', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#3a5a2a', fontWeight: 600, lineHeight: '1.5' }}>
                  ✅ Check your inbox!
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#3d4a2e', lineHeight: '1.5' }}>
                  A reset link has been sent to <strong>{email}</strong>. It may take a minute to arrive.
                </p>
                <button
                  type="button"
                  onClick={() => setResetSent(false)}
                  style={{ marginTop: '12px', background: 'none', border: 'none', fontSize: '0.72rem', color: '#3a5a2a', cursor: 'pointer', textDecoration: 'underline', fontFamily: "'Montserrat', sans-serif" }}
                >
                  Send again
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -800px 0; }
          100% { background-position: 800px 0; }
        }
        .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
          background-size: 800px 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 8px;
        }
      `}</style>
      {/* Nav skeleton */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '40px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
        {[120, 90, 100, 80, 80].map((w, i) => (
          <div key={i} className="skeleton" style={{ width: w, height: 36, borderRadius: '8px' }} />
        ))}
      </div>
      {/* Header skeleton */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div className="skeleton" style={{ width: 160, height: 12, margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ width: 280, height: 32, margin: '0 auto 12px' }} />
        <div className="skeleton" style={{ width: 220, height: 12, margin: '0 auto' }} />
      </div>
      {/* Milestone bar skeleton */}
      <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', backgroundColor: '#fff', borderRadius: '16px', padding: '24px 32px', marginBottom: '40px', border: '1px solid #f0f0f0' }}>
        {[1,2,3].map((i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '0 24px' }}>
            <div className="skeleton" style={{ width: 48, height: 28 }} />
            <div className="skeleton" style={{ width: 80, height: 10 }} />
          </div>
        ))}
      </div>
      {/* Lesson card skeleton */}
      <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '36px', border: '1px solid #f0f0f0', borderTop: '4px solid #f0f0f0' }}>
        <div className="skeleton" style={{ width: 100, height: 20, borderRadius: '20px', marginBottom: '20px' }} />
        <div className="skeleton" style={{ width: '60%', height: 36, marginBottom: '12px' }} />
        <div className="skeleton" style={{ width: 32, height: 2, marginBottom: '28px' }} />
        <div style={{ display: 'flex', gap: '10px', marginBottom: '28px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ width: 100, height: 56, borderRadius: '10px' }} />)}
        </div>
        <div className="skeleton" style={{ width: '100%', height: 48, borderRadius: '12px' }} />
      </div>
    </div>
  );

  return (
    <div style={dashboardLayout}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
          @keyframes pulseGlow {
  0%, 100% { box-shadow: 0 4px 16px rgba(188,108,37,0.35); }
  50%      { box-shadow: 0 4px 24px rgba(188,108,37,0.6); }
}
      `}</style>


      {/* ── Idle warning modal ── */}
      {showIdleWarning && (
        <div role="dialog" aria-modal="true" aria-label="Avviso di inattività" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(40,54,24,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '40px 32px', maxWidth: '360px', width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>⏰</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.6rem', fontWeight: 600, color: DEEP_MOSS, margin: '0 0 12px' }}>
              Still there?
            </h3>
            <p style={{ fontSize: '0.82rem', color: '#3d4a2e', lineHeight: '1.6', margin: '0 0 28px' }}>
              You've been inactive for a while. For your security, you'll be logged out in <strong>1 minute</strong> unless you're still here.
            </p>
            <button
              onClick={() => setShowIdleWarning(false)}
              style={{ ...loginButtonStyle, width: 'auto', padding: '12px 36px' }}
            >
              I'm still here
            </button>
          </div>
        </div>
      )}

      {/* Reschedule modal */}
      {isRescheduleOpen && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            {rescheduleSuccess ? (
              /* ── Success screen ── */
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>✅</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.6rem', fontWeight: 600, color: DEEP_MOSS, margin: '0 0 10px' }}>
                  Request sent!
                </h3>
                <p style={{ fontSize: '0.82rem', color: '#3d4a2e', lineHeight: '1.6', margin: '0 0 28px' }}>
                  Francesca will confirm your new time as soon as possible. You'll see the update in your lesson card.
                </p>
                <button
                  onClick={() => { setIsRescheduleOpen(false); setRescheduleSuccess(false); }}
                  style={loginButtonStyle}
                >
                  Done
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 500, color: DEEP_MOSS, margin: 0 }}>Request Reschedule</h3>
                  <button onClick={() => { setIsRescheduleOpen(false); setReschedulingLesson(null); setRequestedDate(null); setRescheduleReason(''); setIsSubmitting(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
                </div>

                <p style={{ fontSize: '0.75rem', color: '#3d4a2e', fontStyle: 'italic', marginBottom: '20px' }}>
                  Choose your preferred new date and time. Francesca will confirm as soon as possible.
                </p>

                {/* Date picker */}
                <div style={inputGroup}>
                  <label style={labelStyle}>New Date</label>
                  <select
                    style={inputStyle}
                    defaultValue=""
                    aria-label="Seleziona una nuova data"
                    onChange={(e) => setRequestedDate(prev => ({ ...prev, date: e.target.value }))}
                  >
                    <option value="" disabled>Select a date...</option>
                    {Array.from({ length: 60 }, (_, i) => {
                      const d = new Date();
                      d.setDate(d.getDate() + i + 1);
                      const val = d.toISOString().split('T')[0];
                      const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>

                {/* Time */}
                <div style={inputGroup}>
                  <label style={labelStyle}>Preferred Time</label>
                  <select
                    style={inputStyle}
                    defaultValue=""
                    aria-label="Seleziona un orario preferito"
                    onChange={(e) => {
                      const [hour, minute] = e.target.value.split(':');
                      setRequestedDate(prev => ({ ...prev, hour, minute }));
                    }}
                  >
                    <option value="" disabled>Select a time...</option>
                    {Array.from({ length: 24 }, (_, i) => i).flatMap(h =>
                      ['00', '15', '30', '45'].map(m => {
                        const val = `${String(h).padStart(2, '0')}:${m}`;
                        return <option key={val} value={val}>{val}</option>;
                      })
                    )}
                  </select>
                </div>

                {/* Reason */}
                <div style={inputGroup}>
                  <label style={labelStyle}>Reason (Optional)</label>
                  <textarea
                    style={{ ...inputStyle, height: '70px', resize: 'none' }}
                    aria-label="Motivo (opzionale)"
                    placeholder="e.g. work conflict, travel..."
                    onChange={(e) => setRescheduleReason(e.target.value)}
                  />
                </div>

                <button
                  onClick={() => handleRescheduleSubmit(reschedulingLesson?.id)}
                  disabled={isSubmitting || !requestedDate?.date || !requestedDate?.hour || !requestedDate?.minute}
                  style={{ ...loginButtonStyle, opacity: (isSubmitting || !requestedDate?.date || !requestedDate?.hour || !requestedDate?.minute) ? 0.4 : 1 }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <main style={mainContentArea}>
        <div style={contentMaxContainer}>

          {/* Nav — bottom bar on mobile, horizontal on desktop */}
          <style>{`
            @media (max-width: 768px) {
              #student-nav-bar {
                position: fixed !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                top: auto !important;
                z-index: 9000 !important;
                display: flex !important;
                flex-direction: row !important;
                justify-content: space-around !important;
                align-items: center !important;
                background: #fff !important;
                border-top: 1px solid #eee !important;
                border-bottom: none !important;
                padding: 8px 0 env(safe-area-inset-bottom, 8px) !important;
                gap: 0 !important;
                overflow: visible !important;
                box-shadow: 0 -4px 20px rgba(0,0,0,0.08) !important;
              }
              #student-nav-bar button {
                flex: 1 !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                padding: 6px 4px !important;
                border-radius: 8px !important;
                gap: 3px !important;
                min-width: 0 !important;
              }
              #student-nav-bar button .tab-emoji {
                font-size: 1.3rem !important;
                display: block !important;
              }
              #student-nav-bar button .tab-label {
                display: block !important;
                font-size: 0.65rem !important;
                max-width: 100% !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
              }
              #student-content { padding-bottom: 80px !important; }
              #student-nav-hint { display: none !important; }
            }
            @media (min-width: 769px) {
              #student-nav-bar button .tab-emoji,
              #student-nav-bar button .tab-label {
                display: inline !important;
              }
              #student-nav-bar button .tab-emoji {
                font-size: inherit !important;
                margin-right: 2px !important;
              }
            }
          `}</style>

          <div style={{ position: 'relative', marginBottom: '40px' }}>
            <nav style={horizontalNavScroll} id="student-nav-bar" aria-label="Student navigation">
              {NAV_TABS.map(({ id, label }) => {
                const emoji = label.match(/^\S+/)?.[0] || '';
                const text  = label.replace(/^\S+\s*/, '');
                return (
<button key={id} onClick={() => { setActiveTab(id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    style={{ ...sideTabBtn, ...(activeTab === id ? activeSideTab : { opacity: 0.75, backgroundColor: 'transparent' }) }}>
                    <span className="tab-emoji">{emoji}</span>
                    <span className="tab-label">{text || emoji}</span>
                  </button>
                );
              })}
            </nav>
            <div id="student-nav-hint" style={{
              position: 'absolute', top: 0, right: 0, bottom: '1px',
              width: '48px',
              background: 'linear-gradient(to right, transparent, #fcfcfc)',
              pointerEvents: 'none',
            }} />
          </div>

          <div id="student-content">

          {/* ── Dashboard ── */}
          {activeTab === 'dashboard' && (
            <section style={{ animation: 'fadeIn 0.5s ease' }}>
              <header style={{ ...headerStyle, marginBottom: '32px', borderRadius: '16px', padding: '32px 24px', background: 'linear-gradient(to right, rgba(0,146,70,0.08) 0%, rgba(0,146,70,0.08) 33.3%, transparent 33.3%, transparent 66.6%, rgba(206,43,55,0.08) 66.6%, rgba(206,43,55,0.08) 100%), #fff' }}>
                <p style={kickerStyle}>Your Space. Your Pace.</p>
                <h2 style={{ ...titleStyle, fontSize: 'clamp(1.8rem, 5vw, 2.8rem)' }}>
                  {getGreeting()}, {firstName || 'Studente'}!
                </h2>
                <p style={{ fontSize: '0.8rem', color: '#3d4a2e', marginTop: '8px', fontStyle: 'italic' }}>
                  You've got this. Let's keep moving.
                </p>
              </header>
{/* ── Review success message ── */}
{reviewSuccess && (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '20px 24px', marginBottom: '28px',
    backgroundColor: '#f0f7ec', borderRadius: '14px',
    border: '2px solid rgba(96,108,56,0.2)',
    borderLeft: '4px solid #606C38',
    boxShadow: '0 4px 16px rgba(40,54,24,0.06)',
  }}>
    <span style={{ fontSize: '1.4rem' }}>🤍</span>
    <div>
      <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontStyle: 'italic', fontWeight: 600, color: DEEP_MOSS, lineHeight: 1.3 }}>
        Grazie mille!
      </p>
      <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#606C38', lineHeight: 1.5, fontWeight: 600 }}>
        Your review has been received. It means everything. 🇮🇹
      </p>
    </div>
  </div>
)}
{/* ── Review nudge banner ── */}
{/* ── Review nudge banner ── */}
{!reviewSubmitted && past.length >= 3 && (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '16px', padding: '20px 24px', marginBottom: '28px',
    backgroundColor: '#fff',
    borderRadius: '14px',
    border: `2px solid rgba(40,54,24,0.12)`,
    borderLeft: `5px solid ${DEEP_MOSS}`,
    boxShadow: '0 6px 22px rgba(40,54,24,0.1)',
    flexWrap: 'wrap',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <span style={{ fontSize: '1.6rem' }}>🇮🇹</span>
      <div>
        <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem', fontStyle: 'italic', fontWeight: 600, color: DEEP_MOSS, lineHeight: 1.3 }}>
          Enjoying your lessons with Francesca?
        </p>
        <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#606C38', lineHeight: 1.5, fontWeight: 700, letterSpacing: '0.3px' }}>
          Share your experience with future students.
        </p>
      </div>
    </div>
    <button
      onClick={() => setReviewModalOpen(true)}
      style={{
        flexShrink: 0, padding: '11px 24px', borderRadius: '50px',
        border: 'none', backgroundColor: DEEP_MOSS,
        color: '#fff', fontSize: '0.72rem', fontWeight: 700,
        cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
        letterSpacing: '0.5px', whiteSpace: 'nowrap',
        boxShadow: '0 4px 14px rgba(40,54,24,0.3)',
      }}>
      ✍️ Leave a review
    </button>
  </div>
)}
<div
  onClick={() => unpaidCount > 0 && document.getElementById('payment-summary')?.scrollIntoView({ behavior: 'smooth' })}
  style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px 16px', border: '1px solid rgba(188,108,37,0.08)', boxShadow: '0 2px 12px rgba(40,54,24,0.03)', marginBottom: '40px', cursor: unpaidCount > 0 ? 'pointer' : 'default' }}>

<div style={{
  display: 'grid',
  gridTemplateColumns: unpaidCount > 0 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '0',
}}>
  <div style={milestoneItem}>
    <span style={milestoneNumber}>{progressPercentage}%</span>
    <span style={milestoneLabel}>Roadmap to Fluency</span>
    <div style={miniBarBg}><div style={{ ...miniBarFill, width: `${progressPercentage}%` }} /></div>
  </div>
  <div style={{ ...milestoneItem, borderLeft: '1px solid #eee', borderRight: unpaidCount > 0 ? '1px solid #eee' : 'none' }}>
    <span style={milestoneNumber}>{past.length}</span>
    <span style={milestoneLabel}>Lessons completed</span>
  </div>
  {unpaidCount > 0 && (
    <div style={milestoneItem}>
      <span style={{ ...milestoneNumber, color: '#BC6C25' }}>{unpaidCount}</span>
      <span style={{ ...milestoneLabel, color: '#BC6C25' }}>Due</span>
    </div>
  )}
</div>
</div>
              {!featuredLesson ? (
                past.length === 0 && allWords.length === 0 ? (
                  // ── BRAND NEW STUDENT — onboarding ──
                  <section style={{ ...noLessonCardStyle, textAlign: 'left', padding: 'clamp(28px, 5vw, 48px)' }}>
                    <p style={{ color: TERRACOTTA, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 700, marginBottom: '8px' }}>Ciao!</p>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 400, fontStyle: 'italic', color: DEEP_MOSS, margin: '0 0 8px' }}>
                      Your Italian journey starts here.
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: '#3d4a2e', lineHeight: '1.7', margin: '0 0 32px', maxWidth: '480px' }}>
                      This is your personal learning space. Here's everything you'll find once your lessons begin.
                    </p>
                    {/* How it works steps */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '36px' }}>
                      {[
                        { icon: '📅', title: 'Book a lesson', desc: 'Pick a time with Francesca via Calendly. She\'ll confirm it and it\'ll appear right here on your dashboard.' },
                        { icon: '📇', title: 'Build your vocabulary', desc: 'After each lesson, new words are added to your personal dictionary. Practice with flashcards and quizzes.' },
                        { icon: '📍', title: 'Track your progress', desc: 'Your Roadmap shows every topic you\'ve covered on your path to fluency.' },
                        { icon: '📚', title: 'Access your resources', desc: 'Homework, notes, and materials from Francesca appear in the Risorse tab — download them before they expire.' },
                      ].map(({ icon, title, desc }) => (
                        <div key={title} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(188,108,37,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                            {icon}
                          </div>
                          <div>
                            <p style={{ margin: '0 0 3px', fontSize: '0.82rem', fontWeight: 700, color: DEEP_MOSS }}>{title}</p>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#3d4a2e', lineHeight: '1.6' }}>{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ height: '1px', backgroundColor: 'rgba(188,108,37,0.1)', margin: '0 0 28px' }} />
                    <p style={{ fontSize: '0.82rem', color: DEEP_MOSS, margin: '0 0 16px', fontWeight: 500 }}>Ready to book your first lesson?</p>
                    <div className="calendly-button-fix">
                      <PopupButton url={CALENDLY_URL} rootElement={document.getElementById('root')} text="📅 Book Your First Lesson" styles={customCalendlyStyles} />
                    </div>
                  </section>
                ) : (
                  // ── RETURNING STUDENT, NO UPCOMING LESSON ──
                  <section style={noLessonCardStyle}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🇮🇹</div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 400, fontStyle: 'italic', color: DEEP_MOSS, margin: '0 0 12px' }}>
                      Pronto per la prossima lezione?
                    </h3>
                    <p style={{ fontSize: '0.85rem', color: '#3d4a2e', lineHeight: '1.6', margin: '0 0 8px' }}>
                      Just scheduled? Give Francesca a moment to confirm. Your lesson card will appear here shortly.
                    </p>
                    <div style={{ height: '1px', backgroundColor: 'rgba(188,108,37,0.1)', margin: '24px 0' }} />
                    <p style={{ fontSize: '0.85rem', color: DEEP_MOSS, margin: '0 0 20px', opacity: 0.8 }}>
                      Not booked yet? Pick a time and let's get started!
                    </p>
                    <div className="calendly-button-fix">
                      <PopupButton url={CALENDLY_URL} rootElement={document.getElementById('root')} text="📅 Book Your Next Lesson" styles={customCalendlyStyles} />
                    </div>
                  </section>
                )
                ) : new Date(featuredLesson.created_at) > now ? (
                // ── UPCOMING LESSON ──
                <section id={`lesson-${featuredLesson.id}`} style={upcomingFeaturedCard}>
                  {/* Top row */}
                  <div style={{ marginBottom: '24px' }}>
                    <span style={upcomingPillBadge}>✦ Prossima lezione</span>
                  </div>

                  {/* Topic — hero */}
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', fontWeight: 400, fontStyle: 'italic', color: DEEP_MOSS, margin: '0 0 8px', lineHeight: 1.2 }}>
                    {featuredLesson.topic_studied || 'Lesson to be defined'}
                  </h3>
                  <div style={{ width: '32px', height: '2px', backgroundColor: TERRACOTTA, margin: '0 0 28px', borderRadius: '2px' }} />

                  {/* Date + time chips */}
                  {/* Date + time chips */}
<div style={{ display: 'flex', gap: '8px', marginBottom: '28px', flexWrap: 'wrap', alignItems: 'stretch' }}>
  <div style={upcomingInfoChip}>
    <span style={{ fontSize: '0.75rem', letterSpacing: '2px', color: '#3d4a2e', display: 'block', marginBottom: '3px' }}>DAY</span>
    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: DEEP_MOSS }}>
      {new Date(featuredLesson.created_at).toLocaleDateString('en-US', { weekday: 'long' })}
    </span>
  </div>
  <div style={upcomingInfoChip}>
    <span style={{ fontSize: '0.75rem', letterSpacing: '2px', color: '#3d4a2e', display: 'block', marginBottom: '3px' }}>DATE & TIME</span>
    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: DEEP_MOSS }}>
      {new Date(featuredLesson.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
      {' · '}
      <span style={{ color: TERRACOTTA }}>
        {new Date(featuredLesson.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
      </span>
    </span>
    <span style={{ fontSize: '0.65rem', color: '#aaa', display: 'block', marginTop: '2px' }}>
      {Intl.DateTimeFormat().resolvedOptions().timeZone}
    </span>
  </div>
</div>



                  {/* Notes from Francesca — hide if it's just a system confirmation */}
                  {featuredLesson.personal_notes && featuredLesson.personal_notes.replace('[CONFIRMED]', '').trim() && (
                    <div style={{ marginBottom: '24px', padding: '16px 20px', backgroundColor: PAPER, borderRadius: '12px', borderLeft: `3px solid ${TERRACOTTA}` }}>
                      <p style={{ fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, margin: '0 0 8px', fontWeight: 700 }}>
                        Note da Francesca
                      </p>
                      <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem', fontStyle: 'italic', color: '#3d4a2e', margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                        "{featuredLesson.personal_notes.replace('[CONFIRMED]', '').trim()}"
                      </p>
                      {isConfirmedNote(featuredLesson.personal_notes) && (
                        <span style={{ marginTop: '8px', display: 'inline-block', fontSize: '0.75rem', backgroundColor: '#f0f7ec', color: '#4A5D23', padding: '3px 10px', borderRadius: '20px', fontWeight: 700, letterSpacing: '0.5px' }}>
                          ✓ new time confirmed
                        </span>
                      )}
                    </div>
                  )}
                  {/* Show just the badge when note is only [CONFIRMED] with no extra text */}
                  {featuredLesson.personal_notes && !featuredLesson.personal_notes.replace('[CONFIRMED]', '').trim() && isConfirmedNote(featuredLesson.personal_notes) && (
                    <div style={{ marginBottom: '24px' }}>
                      <span style={{ display: 'inline-block', fontSize: '0.75rem', backgroundColor: '#f0f7ec', color: '#4A5D23', padding: '5px 14px', borderRadius: '20px', fontWeight: 700, letterSpacing: '0.5px' }}>
                        ✓ New time confirmed
                      </span>
                    </div>
                  )}

                  {/* JOIN button */}
                  {meetingLink && (
                    <a href={meetingLink} target="_blank" rel="noreferrer" style={joinBigBtnStyle}>
                      🎥 Join Classroom
                    </a>
                  )}

                  {/* 24h policy + reschedule — prominent banner */}
                  {!isLocked(featuredLesson.created_at) ? (
                    <div style={{ marginTop: '16px', padding: '14px 18px', backgroundColor: '#FFF8F0', borderRadius: '10px', border: '1px solid rgba(188,108,37,0.25)', display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>⏰</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ color: DEEP_MOSS, fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
                          Rescheduling must be requested at least <strong>24 hours</strong> before the session. Late cancellations are charged in full.
                        </p>
                      </div>
                      <button
                        onClick={() => { setReschedulingLesson(featuredLesson); setIsRescheduleOpen(true); }}
                        style={{ flexShrink: 0, backgroundColor: '#fff', border: `1.5px solid ${TERRACOTTA}`, color: TERRACOTTA, padding: '8px 16px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
                      >
                        Reschedule
                      </button>
                    </div>
                  ) : (
                    <div style={{ marginTop: '16px', padding: '14px 18px', backgroundColor: '#fff5f5', borderRadius: '10px', border: '1px solid rgba(217,83,79,0.2)', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🔒</span>
                      <p style={{ color: '#c53030', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
                        <strong>Schedule locked.</strong> Rescheduling is no longer available within 24 hours. The session will be charged in full.{' '}
                        For genuine emergencies contact Francesca at{' '}
                        <a href="mailto:sayciao.school@outlook.com" style={{ color: '#c53030', fontWeight: 600 }}>sayciao.school@outlook.com</a>.
                      </p>
                    </div>
                  )}
                </section>
              ) : (
                // ── LAST LESSON — warm, reflective ──
                <section style={pastFeaturedCard}>
                  <span style={pastPillBadge}>↩ Ultima lezione</span>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 400, fontStyle: 'italic', color: DEEP_MOSS, margin: '16px 0 6px', lineHeight: 1.2 }}>
                    {featuredLesson.topic_studied || 'General Lesson'}
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: '#3d4a2e', margin: '0 0 20px' }}>
                    {new Date(featuredLesson.created_at).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    <span style={{ color: TERRACOTTA, marginLeft: '6px' }}>
                      at {new Date(featuredLesson.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>

                  {featuredLesson.personal_notes && (
                    <div style={{ padding: '16px 20px', backgroundColor: PAPER, borderRadius: '12px', borderLeft: `3px solid rgba(188,108,37,0.3)`, marginBottom: '20px' }}>
                      <p style={{ fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, margin: '0 0 8px', fontWeight: 700 }}>
                        Maestra's notes
                      </p>
                      <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem', fontStyle: 'italic', color: '#3d4a2e', margin: 0, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                        "{featuredLesson.personal_notes.replace('[CONFIRMED]', '').trim()}"
                      </p>
                    </div>
                  )}

                  {meetingLink && (
                    <a href={meetingLink} target="_blank" rel="noreferrer" style={joinOutlineBtnStyle}>
                      🎥 Re-join classroom
                    </a>
                  )}

                  <p style={{ marginTop: '20px', fontSize: '0.75rem', color: '#3d4a2e', fontStyle: 'italic' }}>
                    * Lessons may be rescheduled up to 24 hours before the session begins.
                  </p>
                </section>
              )}

              {upcoming.length > 0 && (
                <section id="booking" style={compactBookingSuite}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={kickerStyle}>Ready for your next lesson?</p>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: 500, color: DEEP_MOSS, marginBottom: '15px' }}>📅 Pick a time</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#FFF8F0', border: '1px solid rgba(188,108,37,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', textAlign: 'left' }}>
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>⏰</span>
                      <p style={{ fontSize: '0.78rem', color: DEEP_MOSS, margin: 0, lineHeight: '1.5' }}>
                        <strong>24hour notice required.</strong> All rescheduling requests must be sent at least 24 hours before the session. Late cancellations are charged in full.
                      </p>
                    </div>
                    <div className="calendly-button-fix">
                      <PopupButton url={CALENDLY_URL} rootElement={document.getElementById('root')} text="📅 Pick a time" styles={customCalendlyStyles} />
                    </div>
                  </div>
                </section>
              )}

              {nextLessons.length > 0 && (
                <div style={{ marginTop: '40px', marginBottom: '40px' }}>
                  <h4 style={{ ...subTitleStyle, textAlign: 'left', color: '#BC6C25', marginBottom: '15px' }}>🗓️ Next Scheduled Lessons</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
{nextLessons.map(lesson => (
                      <div key={lesson.id} id={`lesson-${lesson.id}`} style={upcomingRowStyle}>
                        <div style={dateBadge}>
                          <span style={dateMonth}>{new Date(lesson.created_at).toLocaleDateString('en-US', { month: 'short' })}</span>
                          <span style={dateDay}>{new Date(lesson.created_at).toLocaleDateString('en-US', { day: 'numeric' })}</span>
                          <span style={{ fontSize: '0.75rem', color: '#3d4a2e', marginTop: '2px' }}>{new Date(lesson.created_at).toLocaleDateString('en-US', { year: 'numeric' })}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={upcomingTopicTitle}>{lesson.topic_studied || 'Lesson to be defined'}</h5>
                          <p style={upcomingTimeText}>
                            <span style={{ color: '#BC6C25', fontWeight: 600 }}>{new Date(lesson.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                            <span style={{ fontSize: '0.75rem', color: '#3d4a2e', marginLeft: '6px' }}>({Intl.DateTimeFormat().resolvedOptions().timeZone})</span>
                            <span style={{ margin: '0 8px', opacity: 0.75 }}>•</span>
                            Online Class
                          </p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
  <div style={statusBadge}>Confirmed</div>
  {!isLocked(lesson.created_at) && (
    <button onClick={() => { setReschedulingLesson(lesson); setIsRescheduleOpen(true); }} style={rescheduleBtnStyle}>
      Reschedule
    </button>
  )}
</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              
{/* ── Review modal ── */}
{reviewModalOpen && (
  <div style={modalOverlay}>
    <div style={{ ...modalContent, maxWidth: '460px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, fontWeight: 700 }}>
            LA TUA OPINIONE
          </p>
          <h3 style={{ margin: '4px 0 0', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: 600, color: DEEP_MOSS }}>
            Leave a review
          </h3>
        </div>
        <button onClick={() => setReviewModalOpen(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#aaa' }}>✕</button>
      </div>

      <form onSubmit={handleReviewSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={labelStyle}>Your name</label>
          <input
            style={{ ...inputStyle, backgroundColor: '#fafafa', color: '#aaa' }}
            value={user.user_metadata?.full_name || firstName}
            readOnly
          />
        </div>
        <div>
          <label style={labelStyle}>Where are you from?</label>
          <input
            style={inputStyle}
            placeholder="e.g. New York, NY"
            value={reviewForm.location}
            onChange={(e) => setReviewForm(f => ({ ...f, location: e.target.value }))}
          />
        </div>
        <div>
          <label style={labelStyle}>One word to describe your experience</label>
          <input
            style={inputStyle}
            placeholder="e.g. Life-changing, Fun, Patient..."
            value={reviewForm.tag}
            onChange={(e) => setReviewForm(f => ({ ...f, tag: e.target.value }))}
          />
        </div>
        <div>
          <label style={labelStyle}>Your review</label>
          <textarea
            style={{ ...inputStyle, height: '110px', resize: 'none', paddingTop: '10px' }}
            placeholder="Tell others about your experience learning Italian with Francesca..."
            value={reviewForm.text}
            onChange={(e) => setReviewForm(f => ({ ...f, text: e.target.value }))}
            required
          />
        </div>
        <button
          type="submit"
          disabled={reviewSubmitting || !reviewForm.text.trim()}
          style={{ ...loginButtonStyle, opacity: (reviewSubmitting || !reviewForm.text.trim()) ? 0.4 : 1 }}>
          {reviewSubmitting ? 'Sending...' : '✍️ Submit review'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#aaa', margin: 0 }}>
          Your experience might be exactly what someone else needs to hear. Grazie mille 🤍
        </p>
      </form>
    </div>
  </div>
)}
              {archiveLessons.length > 0 && (
                <div id="payment-summary" style={{ marginTop: '50px' }}>
                  <h4 style={subTitleStyle}>Your lesson journal</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {archiveLessons.map(lesson => (
                      <div key={lesson.id} style={journalRowStyle}>
                        <div style={journalDateBadge}>
                          <span style={dateMonth}>{new Date(lesson.created_at).toLocaleDateString('en-US', { month: 'short' })}</span>
                          <span style={dateDay}>{new Date(lesson.created_at).toLocaleDateString('en-US', { day: 'numeric' })}</span>
                          <span style={{ fontSize: '0.75rem', color: '#3d4a2e', marginTop: '2px' }}>{new Date(lesson.created_at).toLocaleDateString('en-US', { year: 'numeric' })}</span>
                        </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0 0 4px', fontSize: '0.9rem', fontWeight: 600, color: DEEP_MOSS }}>
                            {lesson.topic_studied || 'General Lesson'}
                          </p>
{lesson.late_cancellation && (
  <span style={lesson.paid ? lateCancellationPaidNoteStyle : lateCancellationNoteStyle}>
    {lesson.paid ? 'Late cancellation' : '⚠️ Late cancellation · still due'}
  </span>
)}
                          {lesson.personal_notes && (
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#3d4a2e', fontStyle: 'italic', lineHeight: '1.5',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px' }}>
                              {lesson.personal_notes.replace('[CONFIRMED]', '').trim() || ''}
                            </p>
                          )}
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {lesson.paid
                            ? <span style={studentPaidBadge}>✓ Paid</span>
                            : <PayButton />
                          }
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              )}
            </section>
          )}

          {/* ── Roadmap ── */}
          {activeTab === 'roadmap' && (
            <section style={{ animation: 'fadeIn 0.5s ease', paddingBottom: '50px' }}>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h3 style={{ ...titleStyle, fontSize: '1.8rem' }}>Your Roadmap</h3>
                <p style={{ fontSize: '0.9rem', color: '#3d4a2e' }}>{completedCount} topics mastered</p>
              </div>

              {/* Empty state — zero mastered topics */}
              {completedCount === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 24px', backgroundColor: '#fff', borderRadius: '20px', border: '2px dashed rgba(188,108,37,0.2)', marginBottom: '40px' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🗺️</div>
                  <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.6rem', fontWeight: 400, fontStyle: 'italic', color: DEEP_MOSS, margin: '0 0 10px' }}>
                    Your journey is just beginning.
                  </h4>
                  <p style={{ fontSize: '0.82rem', color: '#3d4a2e', lineHeight: '1.7', maxWidth: '420px', margin: '0 auto' }}>
                    As you complete topics with Francesca, they'll be marked here. This is your personal map to fluency — every ✓ is a step closer.
                  </p>
                </div>
              )}

              {(SYLLABUS || []).map((module, mIndex) => (
                <div key={mIndex} style={{ marginBottom: '60px' }}>
                  <div style={{ borderLeft: '4px solid #BC6C25', paddingLeft: '20px', marginBottom: '30px' }}>
                    <h2 style={{ ...titleStyle, fontSize: '1.4rem', color: '#BC6C25' }}>{module.moduleTitle}</h2>
                    <p style={{ fontSize: '0.9rem', color: '#3d4a2e', fontStyle: 'italic' }}>{module.description}</p>
                  </div>
              {(module.subLevels || []).map(group => {
                      const levelTopicsDone = (group.topics || []).filter(t => masteredSet.has(t)).length;
                      const levelTotal = (group.topics || []).length;
                      const levelPct = levelTotal > 0 ? Math.round((levelTopicsDone / levelTotal) * 100) : 0;
                      const isFullyMastered = levelPct === 100;
                      const isInProgress = levelPct > 0 && levelPct < 100;
                      return (
                    <div key={group.level} style={{ ...syllabusGroupStyle, backgroundColor: isFullyMastered ? '#f0f7ec' : isInProgress ? '#fffdf9' : '#fff', borderColor: isFullyMastered ? 'rgba(96,108,56,0.2)' : isInProgress ? 'rgba(188,108,37,0.15)' : 'rgba(188,108,37,0.1)', borderTop: isFullyMastered ? '4px solid #606C38' : isInProgress ? `4px solid ${TERRACOTTA}` : '1px solid rgba(188,108,37,0.1)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                        <h4 style={{ ...levelHeaderStyle, color: isFullyMastered ? '#606C38' : TERRACOTTA, marginBottom: 0 }}>{group.level}</h4>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isFullyMastered ? '#606C38' : isInProgress ? TERRACOTTA : '#bbb', backgroundColor: isFullyMastered ? '#d4edda' : isInProgress ? 'rgba(188,108,37,0.1)' : '#f5f5f5', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                          {isFullyMastered ? '✓ Completato' : `${levelTopicsDone} / ${levelTotal}`}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.8rem', color: '#3d4a2e', marginBottom: '8px' }}>{group.goal}</p>
                      <div style={{ width: '100%', height: '3px', backgroundColor: '#eee', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
                        <div style={{ height: '100%', borderRadius: '10px', backgroundColor: isFullyMastered ? '#606C38' : TERRACOTTA, width: `${levelPct}%`, transition: 'width 0.5s ease' }} />
                      </div>
                      <div style={checklistContainer}>
                        {(group.topics || []).map(topic => {
                          const isDone = masteredSet.has(topic);
                          return (
                            <div key={topic} style={{ ...checkItemStyle, opacity: isDone ? 1 : 0.45 }}>
                              <div style={{ ...checkboxCircle, backgroundColor: isDone ? '#283618' : 'transparent', borderColor: isDone ? '#283618' : '#ddd' }}>{isDone && '✓'}</div>
                              <span style={{ fontWeight: isDone ? 600 : 400 }}>{topic}</span>
                            </div>
                          );
                        })}
                      </div>
                      </div>
                    );
                  })}
              </div>
            ))}
            </section>
          )}

          {/* ── Vocabolario ── */}
          {activeTab === 'vocabolario' && (
            <VocabolarioTab newWords={newWords} allWords={allWords} firstName={firstName} />
          )}

          {/* ── Risorse ── */}
          {activeTab === 'resources' && (
            <section style={{ animation: 'fadeIn 0.5s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h3 style={{ ...titleStyle, fontSize: '1.8rem' }}>Risorse</h3>
                <div style={{ maxWidth: '550px', margin: '16px auto 0', padding: '14px 20px', backgroundColor: '#FFF8F0', borderRadius: '10px', border: '1px solid rgba(188,108,37,0.25)', display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left' }}>
                  <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⚠️</span>
                  <p style={{ color: '#283618', fontSize: '0.82rem', margin: 0, lineHeight: '1.6' }}>
                    <strong>Save your files!</strong> Resources are refreshed every 7 days and removed automatically. Please download anything you want to keep to your own device before then.
                  </p>
                </div>
              </div>

              {/* Expiry urgent banner */}
              {resources.some(r => {
                const daysOld = (Date.now() - new Date(r.created_at).getTime()) / (1000 * 60 * 60 * 24);
                return daysOld >= 4;
              }) && (
                <div style={{ maxWidth: '600px', margin: '0 auto 24px', padding: '14px 18px', backgroundColor: '#fff5f5', borderRadius: '10px', border: '1px solid rgba(217,83,79,0.25)', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🔴</span>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#c53030', lineHeight: '1.5' }}>
                    <strong>Some files expire soon!</strong> Download them now before they're removed automatically.
                  </p>
                </div>
              )}

              <div style={resourceListStyle}>
                {resources.length > 0 ? resources.map(res => {
                  const daysOld      = (Date.now() - new Date(res.created_at).getTime()) / (1000 * 60 * 60 * 24);
                  const daysLeft     = Math.max(0, Math.ceil(7 - daysOld));
                  const isUrgent     = daysLeft <= 3;
                  const isCritical   = daysLeft <= 1;
                  return (
                    <button
                      key={res.id}
                      onClick={async () => {
                        const { data, error } = await supabase.storage.from('resources').createSignedUrl(res.file_url, 60 * 60);
                        if (error || !data?.signedUrl) { alert('Could not open file. Please try again.'); return; }
                        window.open(data.signedUrl, '_blank', 'noreferrer');
                      }}
                      style={{ ...resourceCardStyle, border: isUrgent ? `1px solid rgba(217,83,79,0.3)` : '1px solid #f0f0f0', backgroundColor: isCritical ? '#fff9f9' : '#fff', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                      <div style={resourceIconStyle}>{res.category === 'Video' ? '📽️' : res.category === 'Audio' ? '🎧' : '📄'}</div>
                      <div style={{ flex: 1 }}>
                        <span style={resourceCategoryStyle}>{res.category || 'Documento'}</span>
                        <h4 style={resourceTitleStyle}>{res.title}</h4>
                        {res.note && (
    <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: TERRACOTTA, fontStyle: 'italic', fontWeight: 600 }}>
      📌 {res.note}
    </p>
  )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <div style={downloadArrowStyle}>→</div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isCritical ? '#c53030' : isUrgent ? TERRACOTTA : '#bbb', whiteSpace: 'nowrap' }}>
                          {isCritical ? '⚠️ expires tomorrow' : isUrgent ? `⏳ ${daysLeft}d left` : `${daysLeft}d left`}
                        </span>
                      </div>
                    </button>
                  );
                }) : (
                  <div style={{ textAlign: 'center', marginTop: '40px' }}>
                    <p style={{ opacity: 0.75 }}>Your personalized resources will appear here soon.</p>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '40px', textAlign: 'center', borderTop: '1px solid #eee', paddingTop: '30px' }}>
                <button onClick={handleEmailSubmit} style={loginButtonStyle}>✍️ Submit Homework via Email</button>
                <p style={{ marginTop: '20px', fontSize: '0.75rem', color: '#283618', opacity: 0.7, fontStyle: 'italic' }}>
                  Or send directly to{' '}
                  <a href={`mailto:${MAESTRA_EMAIL}`} style={{ color: '#BC6C25', textDecoration: 'none', fontWeight: 600 }}>{MAESTRA_EMAIL}</a>
                </p>
              </div>
            </section>
          )}

          {/* ── Verbi ── */}
          {activeTab === 'verbi' && (
            <section style={{ animation: 'fadeIn 0.5s ease' }}>
              <VerbQuiz firstName={firstName} />
            </section>
          )}

          </div>{/* end student-content */}
        </div>
      </main>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const TERRACOTTA = '#BC6C25';
const DEEP_MOSS  = '#283618';
const PAPER      = '#FCF9F2';

const modalOverlay             = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(40, 54, 24, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalContent             = { backgroundColor: '#fff', padding: '30px', borderRadius: '15px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' };
const upcomingRowStyle = { display: 'flex', alignItems: 'center', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid rgba(188, 108, 37, 0.1)', gap: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', flexWrap: 'wrap' };
const dateBadge                = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: PAPER, padding: '8px', borderRadius: '8px', minWidth: '55px', border: '1px solid rgba(188, 108, 37, 0.05)' };
const dateMonth = { fontSize: '0.78rem', textTransform: 'uppercase', color: TERRACOTTA, fontWeight: 'bold', letterSpacing: '1px' };
const dateDay                  = { fontSize: '1.2rem', fontWeight: 600, color: DEEP_MOSS, lineHeight: '1.1' };
const upcomingTopicTitle       = { margin: 0, fontSize: '1rem', color: DEEP_MOSS, fontWeight: 500 };
const upcomingTimeText = { margin: '4px 0 0 0', fontSize: '0.85rem', color: '#3d4a2e' };
const statusBadge              = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#606C38', backgroundColor: '#FEFAE0', padding: '5px 10px', borderRadius: '20px', fontWeight: 'bold' };
const dashboardLayout          = { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#fcfcfc' };
const horizontalNavScroll      = { display: 'flex', gap: '8px', overflowX: 'auto', padding: '15px 5px 10px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(188,108,37,0.2) transparent', msOverflowStyle: 'auto', borderBottom: '1px solid #f0f0f0', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', zIndex: 1, position: 'relative' };
const sideTabBtn               = { background: 'transparent', border: 'none', outline: 'none', textAlign: 'center', padding: '12px 20px', borderRadius: '8px', fontSize: '0.85rem', color: DEEP_MOSS, cursor: 'pointer', transition: 'all 0.2s ease', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap' };
const activeSideTab            = { backgroundColor: TERRACOTTA, color: '#fff', fontWeight: 700, opacity: 1, boxShadow: '0 4px 12px rgba(188, 108, 37, 0.15)' };
const mainContentArea          = { flex: 1, padding: '40px 20px', minHeight: '100vh' };
const contentMaxContainer      = { maxWidth: '900px', margin: '0 auto' };
const journalRowStyle          = { display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 18px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid rgba(188,108,37,0.08)', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' };
const journalDateBadge         = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: PAPER, padding: '8px 10px', borderRadius: '10px', minWidth: '50px', flexShrink: 0 };
const checkboxCircle           = { width: '22px', height: '22px', borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: '#fff', marginRight: '15px', transition: 'all 0.3s ease' };
const syllabusGroupStyle       = { backgroundColor: '#fff', padding: '30px', borderRadius: '20px', marginBottom: '20px', border: '1px solid rgba(188, 108, 37, 0.1)', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' };
const levelHeaderStyle         = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', color: TERRACOTTA, marginBottom: '20px', fontWeight: 700 };
const checkItemStyle = { display: 'flex', alignItems: 'center', marginBottom: '15px', fontSize: '1rem', color: '#3d4a2e' };
const checklistContainer       = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' };
const compactBookingSuite      = { marginTop: '50px', backgroundColor: PAPER, padding: '40px', borderRadius: '15px', border: '1px solid rgba(188, 108, 37, 0.15)' };
const inputGroup               = { marginBottom: '20px' };
const labelStyle = { display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#3d4a2e', marginBottom: '8px' };const inputStyle               = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #eee', fontSize: '0.9rem', boxSizing: 'border-box' };
const loginButtonStyle         = { width: '100%', backgroundColor: TERRACOTTA, color: '#fff', border: 'none', padding: '14px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' };
const togglePasswordButtonStyle = { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: TERRACOTTA, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer', padding: '5px', letterSpacing: '0.5px', zIndex: 10 };
const errorTextStyle           = { color: '#B23A3A', fontSize: '0.8rem', marginBottom: '15px', textAlign: 'center' };
const vocabGrid                = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '20px' };
const flashcardContainer = { perspective: '1000px', width: '100%', maxWidth: '380px', height: '220px', cursor: 'pointer', margin: '0 auto' };
const flashcardInner           = { position: 'relative', width: '100%', height: '100%', transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)', transformStyle: 'preserve-3d' };
const flashcardFlipped         = { transform: 'rotateY(180deg)' };
const flashcardFace            = { position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', padding: '15px', boxSizing: 'border-box' };
const flashcardFront           = { backgroundColor: '#fff', border: '1px solid #eee', color: DEEP_MOSS };
const flashcardBack            = { backgroundColor: TERRACOTTA, color: '#fff', transform: 'rotateY(180deg)' };
const vocabLabel               = { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.75, marginBottom: '8px' };
const itWord                   = { fontWeight: 600, fontSize: '1rem', textAlign: 'center' };
const enWord                   = { fontWeight: 400, fontSize: '0.9rem', textAlign: 'center' };
const audioButtonStyle         = { position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' };
const exampleWrapper           = { marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '10px' };
const exampleText              = { fontSize: '0.75rem', fontStyle: 'italic', margin: 0, textAlign: 'center' };
const headerStyle              = { marginBottom: '20px', textAlign: 'center' };
const kickerStyle              = { color: TERRACOTTA, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '10px' };
const titleStyle               = { fontWeight: 300, fontSize: '2.5rem', margin: 0 };
const milestoneNumber          = { fontSize: '1.2rem', fontWeight: 700, color: DEEP_MOSS };
const milestoneLabel = { fontSize: 'clamp(0.52rem, 2.2vw, 0.75rem)', textTransform: 'uppercase', letterSpacing: '1px', color: '#3d4a2e', marginTop: '4px' };
const milestoneItem = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' };
const miniBarBg                = { width: '80px', height: '4px', backgroundColor: '#eee', borderRadius: '10px', marginTop: '8px', overflow: 'hidden' };
const miniBarFill              = { height: '100%', backgroundColor: TERRACOTTA, borderRadius: '10px' };
// Featured lesson cards
const noLessonCardStyle   = { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '20px', border: '2px dashed rgba(188,108,37,0.2)', marginBottom: '30px' };
const upcomingFeaturedCard = { backgroundColor: '#fff', padding: 'clamp(28px, 5vw, 40px)', borderRadius: '20px', marginBottom: '30px', boxShadow: '0 4px 24px rgba(40,54,24,0.07)', border: '1px solid rgba(188,108,37,0.12)', borderTop: `4px solid ${TERRACOTTA}` };
const pastFeaturedCard    = { backgroundColor: '#fff', padding: 'clamp(24px, 4vw, 36px)', borderRadius: '20px', border: '1px solid rgba(188,108,37,0.12)', marginBottom: '30px', boxShadow: '0 4px 20px rgba(40,54,24,0.04)' };
const upcomingPillBadge   = { display: 'inline-block', fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: TERRACOTTA, backgroundColor: 'rgba(188,108,37,0.15)', padding: '4px 12px', borderRadius: '20px', fontWeight: 700 };
const pastPillBadge       = { display: 'inline-block', fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#3d4a2e', backgroundColor: PAPER, padding: '4px 12px', borderRadius: '20px', fontWeight: 700 };
const upcomingInfoChip    = { padding: '10px 16px', backgroundColor: PAPER, borderRadius: '10px', border: '1px solid rgba(188,108,37,0.1)', fontFamily: "'Montserrat', sans-serif" };
const joinBigBtnStyle     = { display: 'block', textAlign: 'center', padding: '16px', backgroundColor: TERRACOTTA, color: '#fff', textDecoration: 'none', borderRadius: '12px', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'Montserrat', sans-serif", boxShadow: '0 6px 20px rgba(188,108,37,0.35)', transition: 'all 0.2s ease' };
const joinOutlineBtnStyle = { display: 'inline-block', padding: '10px 20px', border: `1.5px solid ${DEEP_MOSS}`, color: DEEP_MOSS, textDecoration: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '1px', fontFamily: "'Montserrat', sans-serif" };
const subTitleStyle = { fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '2px', color: '#3d4a2e', marginBottom: '25px', textAlign: 'center' };const resourceListStyle        = { display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '600px', margin: '0 auto' };
const resourceCardStyle        = { display: 'flex', alignItems: 'center', padding: '20px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f0f0f0', textDecoration: 'none', color: 'inherit' };
const resourceIconStyle        = { fontSize: '1.5rem', marginRight: '20px' };
const resourceCategoryStyle    = { fontSize: '0.75rem', textTransform: 'uppercase', color: TERRACOTTA, fontWeight: 700, letterSpacing: '1px' };
const resourceTitleStyle       = { fontSize: '1rem', margin: '4px 0 0 0', color: DEEP_MOSS };
const downloadArrowStyle       = { marginLeft: 'auto', color: TERRACOTTA, fontWeight: 700 };
const rescheduleBtnStyle = {
  background: 'none',
  border: `1px solid ${TERRACOTTA}`,
  color: TERRACOTTA,
  fontSize: '0.68rem',
  borderRadius: '20px',
  padding: '4px 10px',
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 600,
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};
const navBtnStyle        = { padding: '10px 24px', borderRadius: '50px', border: `1.5px solid rgba(188,108,37,0.3)`, backgroundColor: '#fff', color: DEEP_MOSS, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', transition: 'all 0.2s ease' };
// Quiz styles
const quizNudgeStyle = { color: TERRACOTTA, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '8px', fontWeight: 600, display: 'block' };
const startQuizBtn   = { backgroundColor: TERRACOTTA, color: '#fff', border: 'none', padding: '14px 36px', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" };
const vocabModeCardStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '20px 18px', borderRadius: '14px', border: '1.5px solid rgba(188,108,37,0.15)', backgroundColor: '#fff', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", minWidth: '180px', textAlign: 'center', transition: 'all 0.15s ease' };

// Shared dictionary styles — match VerbQuiz
const sharedKicker     = { color: TERRACOTTA, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '4px', fontWeight: 600, display: 'block', marginBottom: '8px' };
const sharedDictSearch = { width: '100%', padding: '10px 0', marginBottom: '18px', border: 'none', borderBottom: `1px solid ${TERRACOTTA}`, outline: 'none', fontSize: '0.88rem', fontFamily: "'Montserrat', sans-serif", color: DEEP_MOSS, background: 'transparent', boxSizing: 'border-box' };

const studentPaidBadge  = { fontSize: '0.75rem', color: '#606C38', backgroundColor: '#f0f7ec', padding: '4px 10px', borderRadius: '20px', fontWeight: 700, fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const lateCancellationNoteStyle = { display: 'block', fontSize: '0.72rem', color: '#c53030', fontWeight: 700, margin: '2px 0 4px' };
const lateCancellationPaidNoteStyle = {
  ...lateCancellationNoteStyle,
  color: '#999',
  fontWeight: 600,
};
const studentPayBtnStyle = { fontSize: '0.75rem', color: '#fff', backgroundColor: TERRACOTTA, padding: '6px 14px', borderRadius: '20px', fontWeight: 700, fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.5px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px', border: 'none', cursor: 'pointer' };
const payDropdownStyle  = { position: 'absolute', right: 0, top: 'calc(100% + 6px)', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', padding: '8px', minWidth: '200px', zIndex: 100 };
const payOptionStyle    = { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', textDecoration: 'none', transition: 'background 0.15s ease', cursor: 'pointer' };

export default StudentDashboard;